// ============================================================
// GAME — core state machine
// ============================================================

import {
  M3_COLS, M3_ROWS, BATTLE_COLS, BATTLE_ROWS, ELEM, ECOL, SPELLS, UCFG, BASES,
  MATCH3_TIMER_SECS, SCHED_TIMER_SECS, MAX_SWAPS_PER_ROUND,
  pointsToLevel, chainStepMult, chainPointsForGroup,
  type LeaderId,
} from './constants.ts';
import { ri, md, clamp, resetId } from './utils.ts';
import { anim } from './Anim.ts';
import { Gem } from './Gem.ts';
import {
  M3Board,
  type ChainStepEvent, type ChainCompleteEvent, type MatchComponent,
  findMatchComponents,
} from './M3Board.ts';
import { BattleUnit, Base, Pos, mkUnit, mkBase, unitFromJson, baseFromJson } from './BattleUnit.ts';
import type { ServerAdapter } from './client/ServerAdapter.ts';
import { LocalServerAdapter } from './client/LocalServerAdapter.ts';
import { NetworkServerAdapter } from './client/NetworkServerAdapter.ts';
import { resolveNetUrl, resolveNetMode, saveNetUrl, clearNetUrl } from './client/netUrl.ts';
import type { BattleEvent, SpellCommand, SpellCastRecord, SwapRequest } from './server/types.ts';
import { _pushModifier, _recomputeScalars } from './server/spell.ts';
import { SeededRng, randomSeed } from './server/rng.ts';
import {
  type TerrainCell,
  type TerrainGrid,
  generateTerrain,
  flipTerrainForView,
  growTransition,
  rainTransition,
} from './server/terrain.ts';
import { Deployment, type DeployedUnit, generateAiUnits } from './Deployment.ts';
import {
  type DeploymentRules,
  DEFAULT_MANUAL_RULES,
  PRESET_RULES,
  DEFAULT_LEADER_ID,
  getLeader,
} from './balance.ts';
import * as ArmyData from './ArmyData.ts';
import { W, OXB, OYB, CB, OXM, OYM, CM, ZBY, ZHY, ZMY } from './canvas.ts';
import { showPrompt } from './prompt.ts';
import { StoryController } from './story/StoryController.ts';
import { getChapter, CHAPTERS } from './story/StoryData.ts';
import type { ChapterDef } from './story/StoryTypes.ts';
import {
  CASUAL_LEVELS,
  getCasualLevel,
  buildTerrainGrid,
  type CasualLevelDef,
} from './casual/CasualLevels.ts';

// STORY_SELECT (7) — chapter picker shown after tapping "Story Mode"
// on the main menu. Story chapters then run through the regular
// DEPLOY → MATCH3 → SCHED → EXEC → OVER pipeline with `storyController`
// hooked in at key boundaries (see startStoryChapter / handleSwap /
// _applyEvent / _endGame and ../story/StoryController.ts).
//
// CASUAL_SELECT (8) — level picker for Casual Mode. Casual levels are
// fully scripted (preset units on both sides + preset terrain) and the
// player only edits their own paths during DEPLOY. Battle itself runs
// through the standard MATCH3 → SCHED → EXEC pipeline with the regular
// single-player AI; see startCasualMode / startCasualLevel and
// `./casual/CasualLevels.ts`.
export const GS = { MENU: 0, DEPLOY: 1, MATCH3: 2, SCHED: 3, EXEC: 4, OVER: 5, LOBBY: 6, STORY_SELECT: 7, CASUAL_SELECT: 8 } as const;
export type GameState = typeof GS[keyof typeof GS];

export interface FX {
  type: string;
  x?: number; y?: number;
  x1?: number; y1?: number; x2?: number; y2?: number;
  text?: string;
  color: string;
  alpha: number;
  timer: number;
  size?: number;
  radius?: number;
  /** For particle effects */
  dx?: number; dy?: number;
  /** For death skull animation */
  scale?: number;
}

export interface AvailSpell {
  el: number;
  lv: number;
}

export class Game {
  state: GameState = GS.MENU;
  round = 0;
  maxRounds = 10;
  units: BattleUnit[] = [];
  bases: Base[] = [];
  deployment: Deployment = new Deployment();
  board: M3Board;
  aiBoard: M3Board;
  pRes: Record<string, number> = {};
  aRes: Record<string, number> = {};
  /**
   * Smoothed counterpart of `pRes` — Renderer uses this for the HUD
   * element progress bars so they "fill up" toward the true value
   * over ~0.3s instead of snapping. Lerped in `update()` each frame.
   * The level NUMBER (`pointsToLevel`) is also derived from this so
   * the label transitions sync with the bar's animation.
   *
   * Cleared in `_startRound` alongside `pRes`.
   */
  pResDisplay: Record<string, number> = {};
  aResDisplay: Record<string, number> = {};
  /**
   * Phase 3e — Client-side prediction.
   *
   * Local log of every swap the player has issued during the current
   * MATCH3 phase, in execution order. Cleared at `_startRound`.
   *
   * Used at MATCH3 → SCHED boundary: client sends `(stats, swapLog)`
   * to the server via `commitMatchResults`; server replays the log
   * against its own seeded `M3Board` mirror and compares to the
   * claimed stats. Mismatch → cheat detected.
   *
   * Single-player / `LocalServerAdapter` paths still maintain this
   * for parity (cost is trivial), but no replay validation runs there.
   */
  swapLog: SwapRequest[] = [];
  pSpells: (SpellCommand | null)[] = [null, null, null];
  aSpells: (SpellCommand | null)[] = [null, null, null];
  spellSlot = -1;
  schedStep: 'slot' | 'target' | 'confirm' = 'slot';

  /**
   * Session 6c follow-up — bottom interaction zone is now a 2-tab view.
   *  - 'match3' tab: 6×6 gem board (interactive in MATCH3, dimmed in SCHED/EXEC)
   *  - 'sched'  tab: scheduling slots + spell picker (interactive in SCHED;
   *                  read-only summary in MATCH3 / EXEC)
   * Auto-switches at phase boundaries; player can manually toggle to peek.
   */
  bottomTab: 'match3' | 'sched' = 'match3';

  /**
   * Session 6c follow-up — when MATCH3 ends (timer expired OR swap cap),
   * defer the SCHED transition until the M3Board's animation queue drains
   * (`!board.locked`). Avoids cutting off mid-cascade visual feedback.
   */
  pendingSched = false;

  /**
   * Session 6c follow-up — once `pendingSched` && board has settled, hold
   * for ~1.4s with a "進入魔法部屬" transition banner before actually
   * flipping to SCHED. Smooths the MATCH3→SCHED handoff (felt jarring
   * before — board went still, tab flicked, no breathing room).
   */
  schedEnterDelay = 0;
  schedEnterTriggered = false;

  /**
   * Session 6c follow-up — sticky banner that shows the current EXEC
   * sub-step (Friendly Spells / Move / Combat / Hostile Spells / Contest).
   * Replaces the prior fade-out floatText approach so the label stays put
   * until the next sub-step replaces it.
   */
  phaseBanner: { text: string; color: string } | null = null;
  selSpellType = 0;
  availSpells: AvailSpell[] = [];
  usedSpellEls: number[] = [];
  timer = 0;
  targeting = false;
  targetPos: Pos | null = null;
  // Execution
  adapter!: ServerAdapter;
  events: BattleEvent[] = [];
  stepIdx = 0;
  stepTimer = 0;
  stepActive = false;
  stepApplied = false;
  /** Phase 3c Step 5: set while executeExec RPC is in flight. Blocks _updateExec. */
  execPending = false;
  /** Phase 3c Step 5: set while initBattle RPC is in flight. Blocks state progression. */
  initPending = false;
  /**
   * Session 6c follow-up — human-readable label shown by the loading
   * overlay (`Renderer._loadingOverlay`) while initPending is true.
   * Defaults to a generic "Connecting" if left unset.
   */
  loadingLabel: string | null = null;
  /** Phase 3c Step 5: latest adapter error surfaced to the HUD (optional UI). */
  adapterError: string | null = null;
  // Visual FX
  fx: FX[] = [];
  winner = -1;
  selectedCell: { r: number; c: number } | null = null;
  pressedCell: { r: number; c: number } | null = null;
  /** Timer for selected-cell pulse animation */
  selPulse = 0;
  /** Resource flash timers for HUD pop animation */
  resFlash: Record<string, number> = {};
  /** Phase transition overlay */
  transitionTimer = 0;
  transitionLabel = '';
  private _aiTimer = 0;
  /** Cached snapshot of save slots — refreshed when entering deploy / after mutations */
  private _slotsCache: (ArmyData.ArmyData | null)[] = [null, null, null];
  /** Pending slot action toggle in deployment UI: 'none' | 'save' | 'clear' */
  slotAction: 'none' | 'save' | 'clear' = 'none';
  /** Phase 3c: whether to route battle through NetworkServerAdapter. */
  netMode = false;
  /**
   * Phase 3c / 5a: server URL when in netMode.
   * Resolved by `resolveNetUrl()` (see `client/netUrl.ts`):
   *   URL param > localStorage > build env > 'ws://localhost:8080'.
   * Mutable: `promptServerUrl()` updates this and persists to localStorage.
   */
  netUrl = 'ws://localhost:8080';
  /**
   * Phase 3c Step 6: master RNG seed for the current battle.
   * Regenerated on each startBattle(); also echoed to the server via
   * initBattle so replays/audits have the exact seed that produced the
   * session. 0 = "no battle yet".
   */
  masterSeed = 0;
  /**
   * Phase 3e: this caller's per-team M3Board seed.
   * Derived in `startBattle()` from the master seed. Shipped to server
   * via `initBattle` so the server can build a `Match3Mirror` for
   * anti-cheat replay validation. 0 = "no battle yet".
   */
  playerBoardSeed = 0;

  /**
   * Terrain (special battlefield cells — grass / puddle / burningGrass).
   * Stored in VIEW coords (already flipped for team-1 guest); Renderer
   * reads it directly without any further coord juggling.
   *
   * Lifecycle:
   *   - Single-player: picked at `startSinglePlayer` so terrain is
   *     visible during DEPLOY for path planning.
   *   - PvP: server sends `terrainSeed` in the createRoom / joinRoom
   *     RPC results; `startCreateRoom` / `startJoinRoom` apply it.
   *     `battleReady` later ships the authoritative grid which we
   *     overwrite the local copy with (defensive — should be byte-
   *     identical to what we generated locally).
   *   - Reset to `null` by `restart()`.
   */
  terrain: TerrainGrid | null = null;

  /**
   * The seed `terrain` was generated from. Echoed to the server in
   * `initBattle` so single-mode authoritative state agrees on the grid.
   * 0 = "no terrain yet".
   */
  terrainSeed = 0;

  // ----------------------------------------------------------
  // Phase 3d Step 3 — PvP mode plumbing
  // ----------------------------------------------------------

  /**
   * Which mode the current session is running in.
   *   - 'single'    : local single-player vs the in-process AI (DEFAULT)
   *   - 'pvp-host'  : created a room, waiting for / paired with a guest
   *   - 'pvp-guest' : joined someone else's room
   *
   * Drives whether _aiMatch3 fires, whether _enterSched auto-fills the
   * opponent's schedule, and which team field every outgoing RPC tags
   * itself with.
   */
  gameMode: 'single' | 'pvp-host' | 'pvp-guest' | 'story' | 'casual' = 'single';

  // ----------------------------------------------------------
  // Casual Mode plumbing
  // ----------------------------------------------------------

  /**
   * Active casual level (non-null only while `gameMode === 'casual'`).
   * Holds the preset unit roster + terrain grid loaded by
   * `startCasualLevel`. Used by:
   *   - `startDeployment` to mirror player units into `deployment.placed`
   *     and stash AI units for `startBattle` to merge.
   *   - `startBattle` to call `LocalServerAdapter.overrideTerrain` after
   *     init so authoritative effect resolution uses the hand-authored
   *     grid (the seed-based generator is bypassed).
   *   - Renderer / OVER button branch to know whether to offer Retry
   *     Level or Back to Casual Select.
   * Cleared by `restart()`.
   */
  casualLevel: CasualLevelDef | null = null;

  /**
   * Scripted-mode AI side roster — DeployedUnit[] (positions in world
   * coords) that startBattle converts to BattleUnit[] for team 1.
   *
   * Populated by `startCasualLevel` (from `level.ai`) and by the story
   * branch of `startDeployment` (from `storyController.consumePendingDeployment().ai`).
   * Consumed once by `startBattle`, then left in place (re-consumed on Retry).
   * Cleared by `restart()`.
   *
   * null in single / PvP — single generates AI at startBattle via
   * `generateAiUnits(rules.composition, rng)`; PvP merges opponent
   * units server-side.
   */
  private _presetAiUnits: DeployedUnit[] | null = null;

  // ----------------------------------------------------------
  // Story Mode plumbing
  // ----------------------------------------------------------

  /**
   * Active story controller (non-null only while `gameMode === 'story'`).
   * Owns dialogue queue + tutorial constraints + chapter script position.
   * Cleared by `restart()`.
   */
  storyController: StoryController | null = null;

  /**
   * Set true right after a successful swap in story mode — `update()`
   * polls for `!board.locked && !anim.busy` and then fires
   * `storyController.onMatchSettle()` exactly once. Reset by the same
   * check so consecutive forced swaps each get their own settle hook.
   */
  private _storySettlePending = false;

  // ----------------------------------------------------------
  // Surrender (MATCH3-only escape hatch — long-press 5s → confirm)
  // ----------------------------------------------------------
  /** Seconds the surrender button has been held this gesture. */
  surrenderHoldTime = 0;
  /** True while player is actively pressing the surrender button. */
  surrenderHolding = false;
  /** True after 5s hold completed — confirm modal is showing. */
  surrenderConfirmActive = false;
  /** Visual hold target in seconds — exposed for renderer progress bar. */
  static readonly SURRENDER_HOLD_SECS = 5;

  /** Which team the local player is. Single player is always 0. */
  myTeam: 0 | 1 = 0;

  /**
   * Session 9 — leader chosen by the local player. Sourced from
   * `deployment.leader` at `startBattle` time and stashed here so the
   * SCHED / HUD / EXEC code paths can read it without re-touching the
   * deployment object. Also persisted into ArmyData via the autosave
   * `onChange` hook.
   */
  playerLeader: LeaderId = DEFAULT_LEADER_ID;
  /**
   * Session 9 — opponent's leader. Per design Q3 the AI / story / casual
   * AI always uses `DEFAULT_LEADER_ID` (AAA); PvP would receive the
   * peer's pick via the battleReady server push (left as a TODO until
   * PvP lobby gets a leader picker).
   */
  aiLeader: LeaderId = DEFAULT_LEADER_ID;

  /** PvP room code. null in single-player. */
  roomCode: string | null = null;

  /** Short status text shown over the lobby state. */
  lobbyMessage: string = '';

  /** True while we're awaiting an async room RPC (createRoom / joinRoom). */
  lobbyPending: boolean = false;

  /**
   * Deployment phase deadline (ms since epoch) sent by the server when
   * a multiplayer room is fully joined. Single-player or pre-join is null.
   */
  deployDeadline: number | null = null;

  /** PvP guest's session id once paired (for diagnostics / Step 5 disconnect handling). */
  opponentSessionId: string | null = null;

  // Phase 3d Step 4 — PvP synchronisation flags
  /**
   * True while we're sitting on a phase boundary waiting for the other
   * player. Renderer overlays "Waiting for opponent" when this is set.
   * Cleared whenever the awaited server push arrives.
   */
  waitingForOpponent: boolean = false;

  /**
   * Set by handleInitBattle response in PvP mode. While true,
   * startBattle has fired initBattle but battleReady push hasn't
   * arrived yet — the round can't start.
   */
  awaitingBattleReady: boolean = false;

  /**
   * Phase 3d Step 5 — set when the server pushes opponentDisconnected.
   * Drives the "Opp disconnected" subtitle on the OVER screen and
   * prevents the handler from running twice.
   */
  opponentLeft: boolean = false;

  /**
   * Set when our OWN WebSocket closed unexpectedly (network drop, server
   * kill, mobile-tab background, etc.) while a PvP session was live.
   * Drives the "(連線中斷)" subtitle on the OVER screen and prevents
   * `_handleConnectionLost` from running twice.
   *
   * Mirror of `opponentLeft` for the disconnected-side: the *other* side
   * of the same network event gets `opponentDisconnected` from the
   * server; this side learns about it locally via the synthetic
   * `connectionLost` event emitted by NetworkServerAdapter.
   */
  selfDisconnected: boolean = false;

  // Phase 3d Step 4 fix: view orientation
  /**
   * True when the local player's view should be flipped 180° relative
   * to server world coords. Currently only true for the PvP guest
   * (team 1) so they see their own units at the bottom in blue and
   * opponent at the top in red — same UX as the host.
   *
   * IMPORTANT: this is the SINGLE source of truth. Inbound server data
   * (units, bases, event positions) is flipped to view coords on
   * receipt; outbound client data (spell targetPos, deployment unit
   * positions in initBattle) is flipped back to world coords just
   * before sending. Renderer & most game logic operate on view coords
   * exclusively — no per-call-site coordinate gymnastics.
   */
  get viewFlipped(): boolean { return this.myTeam === 1; }

  /** View ↔ world along X (self-inverse). */
  viewFlipX(x: number): number {
    return this.viewFlipped ? (BATTLE_COLS - 1 - x) : x;
  }
  /** View ↔ world along Y (self-inverse). */
  viewFlipY(y: number): number {
    return this.viewFlipped ? (BATTLE_ROWS - 1 - y) : y;
  }

  /** Convenience: flip a Pos. */
  private _flipPos(p: Pos): Pos {
    return { x: this.viewFlipX(p.x), y: this.viewFlipY(p.y) };
  }

  /**
   * Apply view flip to all coordinate fields inside every event type.
   * Returns the same array if no flip needed (host / single).
   * Mutates clones — caller-supplied events untouched.
   */
  private _flipEventsForView(events: BattleEvent[]): BattleEvent[] {
    if (!this.viewFlipped) return events;
    const fp = (p: Pos) => this._flipPos(p);
    return events.map((ev: any) => {
      // Deep-clone first so we don't scribble on shared/server objects.
      const e = JSON.parse(JSON.stringify(ev));
      switch (e.type) {
        case 'spell':
          for (const s of e.spells || []) {
            if (s.tgt) s.tgt = fp(s.tgt);
            if (s.cmd?.targetPos) s.cmd.targetPos = fp(s.cmd.targetPos);
            // Session 9 — whirlwind embeds MoveRecord[] with from/to
            // positions; flip them just like top-level MoveEvent does.
            if (Array.isArray(s.moves)) {
              for (const m of s.moves) {
                if (m.from) m.from = fp(m.from);
                if (m.to) m.to = fp(m.to);
              }
            }
          }
          break;
        case 'move':
          for (const m of e.moves || []) { m.from = fp(m.from); m.to = fp(m.to); }
          for (const c of e.contested || []) {
            c.pos = fp(c.pos);
            c.unitAFrom = fp(c.unitAFrom);
            c.unitBFrom = fp(c.unitBFrom);
          }
          break;
        case 'combat':
          for (const g of e.groups || []) {
            for (const a of g.attacks || []) {
              if (a.attackerPos) a.attackerPos = fp(a.attackerPos);
              if (a.targetUnitPos) a.targetUnitPos = fp(a.targetUnitPos);
              if (a.targetBasePos) a.targetBasePos = fp(a.targetBasePos);
            }
          }
          break;
        case 'contestResolve':
          for (const r of e.retreats || []) { r.from = fp(r.from); r.to = fp(r.to); }
          for (const s of e.stays || []) { s.pos = fp(s.pos); }
          break;
        case 'terrainIgnite':
        case 'terrainGrow':
        case 'terrainRain':
        case 'terrainBoulderFall':
        case 'terrainBoulderBreak':
          // a-2 + Session 11 + Session 12 — server emits world-coord
          // cells; guest view flips them so the local terrain grid
          // (already in view coords) mutates at the right indices in
          // `_applyEvent`. Same shape for all five terrain-mutation
          // events (Ignite / Grow / Rain / BoulderFall / BoulderBreak),
          // so a fall-through is safe. terrainGrow / terrainRain carry
          // a `to` field per cell that's invariant under flip; the new
          // boulder events are just `Pos[]`.
          if (Array.isArray(e.cells)) {
            e.cells = e.cells.map((p: Pos) => fp(p));
          }
          break;
        case 'terrainDot':
          // applies are unit-keyed (no positional fields), nothing to flip.
          break;
      }
      return e;
    });
  }

  /**
   * Pre-warmed adapter for the lobby flow. In single-player we still build
   * the adapter inside startBattle (legacy path). In PvP we need the adapter
   * BEFORE startBattle so we can call createRoom / joinRoom from the menu.
   */
  private _lobbyUnsubs: Array<() => void> = [];

  constructor() {
    this.board = new M3Board();
    this.aiBoard = new M3Board();
    this.board._onMatch = (e) => this._onPlayerMatch(e);
    this.board._onChainComplete = (e) => this._onPlayerChainComplete(e);
    this.aiBoard._onMatch = (e) => this._onAIMatch(e);
    this.aiBoard._onChainComplete = (e) => this._onAIChainComplete(e);
    // Phase 5a Step 1: net-mode + URL resolution moved to client/netUrl.ts.
    // Priority: ?net=ws://... URL param > localStorage > VITE_DEFAULT_WS_URL > fallback.
    this.netMode = resolveNetMode();
    this.netUrl = resolveNetUrl();
    if (this.netMode) {
      console.log(`[game] net mode ON — will use NetworkServerAdapter (${this.netUrl})`);
    }
    // Start at main menu
    this.state = GS.MENU;
  }

  /** Transition from Menu to Deployment.
   *
   *  Pre-condition: caller (`startSinglePlayer` / `startCreateRoom` /
   *  `startJoinRoom` / `startStoryChapter` / `startCasualLevel`) has
   *  already called `this.deployment.setRules(...)` with the appropriate
   *  rule set. `setRules` itself does the full reset (clears `placed`,
   *  palette counters, drag state, onChange hook), so this method just
   *  handles mode-specific post-setup (story hooks, casual preset load,
   *  autosave wiring for manual modes). */
  startDeployment(): void {
    this.state = GS.DEPLOY;
    this.slotAction = 'none';
    // Story mode: skip autosave restore + skip onChange autosave wiring
    // (we don't want the scripted preset to clobber the player's saved
    // deployment, and the preset gets applied just below).
    if (this.gameMode === 'story') {
      // Install Story Mode hooks on Deployment so place / path mutations
      // are gated by the active controller constraints. Hooks stay
      // attached for the whole DEPLOY phase; cleared by `restart()`.
      const ctrl = this.storyController;
      if (ctrl) {
        this.deployment._storyPlaceGate = (t, x, y) => ctrl.isPlaceAllowed(t, x, y);
        this.deployment._storyPlaceCommitted = (t, x, y) => ctrl.onUnitPlaced(t, x, y);
        this.deployment._storyPathGate = (curLen, x, y) => ctrl.isPathPointAllowed(curLen, x, y);
        this.deployment._storyPathPointAdded = (newLen) => ctrl.onPathPointAdded(newLen);
        // Path Mode toggle gate — only allow toggling ON, never OFF,
        // AND only when the script actually wants the player to toggle
        // (expectPathToggle active). This blocks two failure modes:
        //   (a) player toggles OFF mid-tutorial → can't draw path
        //   (b) player toggles ON before the tutorial reaches the
        //       expectPathToggle step → script doesn't advance and
        //       expectPath never activates either
        this.deployment._storyPathToggleGate = () =>
          ctrl.expectedPathToggle && !this.deployment.pathMode;
        this.deployment._storyPathToggled = () => ctrl.onPathModeToggled();
      }
      const preset = this.storyController?.consumePendingDeployment();
      if (preset) {
        // Preset rules + loadFromUnits = no cap; player units mirror
        // into `placed` 1:1 so Renderer can draw them and player can
        // edit paths. AI side stashed on Game for startBattle to merge.
        this.deployment.loadFromUnits(preset.player.map((u) => ({
          type: u.type,
          pos: { x: u.pos.x, y: u.pos.y },
          path: u.path.map((p) => ({ x: p.x, y: p.y })),
        })));
        this._presetAiUnits = preset.ai.map((u) => ({
          type: u.type,
          pos: { x: u.pos.x, y: u.pos.y },
          path: u.path.map((p) => ({ x: p.x, y: p.y })),
        }));
      }
      this._refreshSlotsCache();
      return;
    }
    // Casual mode — load the level's preset player units into
    // `deployment.placed` so the player can see + draw paths on them.
    // AI side is stashed on Game for `startBattle` to merge. Skip
    // autosave entirely (preset is the source of truth; player's
    // saved deployments shouldn't leak into a scripted level).
    if (this.gameMode === 'casual' && this.casualLevel) {
      const lvl = this.casualLevel;
      // Rules are already PRESET_RULES (set in startCasualLevel) so
      // loadFromUnits doesn't apply composition caps — the level can
      // ship more units than the standard manual cap.
      this.deployment.loadFromUnits(lvl.player.map((u) => ({
        type: u.type,
        pos: { x: u.pos.x, y: u.pos.y },
        path: u.path.map((p) => ({ x: p.x, y: p.y })),
      })));
      this._presetAiUnits = lvl.ai.map((u) => ({
        type: u.type,
        pos: { x: u.pos.x, y: u.pos.y },
        path: u.path.map((p) => ({ x: p.x, y: p.y })),
      }));
      // Default to Path Mode ON so casual players land straight in the
      // "draw paths" UX — there are no placement decisions left to make.
      this.deployment.pathMode = true;
      this._refreshSlotsCache();
      return;
    }
    // Wire autosave: fire on every deployment mutation. Session 9 —
    // serialize includes `deployment.leader` so the saved snapshot
    // captures the current pick alongside placed units / paths.
    this.deployment.onChange = () => {
      ArmyData.saveAutosave(ArmyData.serialize(this.deployment.placed, this.deployment.leader));
    };
    // Restore last session's deployment if an autosave exists.
    // Session 9 — load the saved leader too so DEPLOY UI shows the
    // last pick (`loadFromUnits` accepts an optional leader override).
    const auto = ArmyData.loadAutosave();
    if (auto) this.deployment.loadFromUnits(ArmyData.toDeployedUnits(auto), auto.leader);
    this._refreshSlotsCache();
  }

  // ----------------------------------------------------------
  // SAVE SLOTS — manual Save/Load during deployment
  // ----------------------------------------------------------

  /** Save the current deployment to manual slot N. Returns success flag. */
  saveToSlot(slot: number): boolean {
    if (this.state !== GS.DEPLOY) return false;
    if (this.deployment.placed.length === 0) {
      this._slotToast('Place at least 1 unit first', '#ff8844');
      return false;
    }
    // Session 9 — capture leader alongside the placed units.
    const ok = ArmyData.saveSlot(slot, ArmyData.serialize(this.deployment.placed, this.deployment.leader));
    if (ok) this._slotToast(`Saved to Slot ${slot + 1}`, '#44ff88');
    else this._slotToast('Save failed', '#ff6644');
    this._refreshSlotsCache();
    return ok;
  }

  /** Load manual slot N into the current deployment. Returns success flag. */
  loadFromSlot(slot: number): boolean {
    if (this.state !== GS.DEPLOY) return false;
    const data = ArmyData.loadSlot(slot);
    if (!data) { this._slotToast(`Slot ${slot + 1} is empty`, '#ff8844'); return false; }
    // Session 9 — restore the saved leader alongside the units.
    this.deployment.loadFromUnits(ArmyData.toDeployedUnits(data), data.leader);
    this._slotToast(`Loaded Slot ${slot + 1}`, '#44ccff');
    return true;
  }

  /** Clear manual slot N. */
  clearSlot(slot: number): void {
    if (this.state !== GS.DEPLOY) return;
    ArmyData.clearSlot(slot);
    this._slotToast(`Cleared Slot ${slot + 1}`, '#ff8844');
    this._refreshSlotsCache();
  }

  /**
   * Dispatch a tap on slot N according to the current slotAction toggle.
   * - 'save'  → saveToSlot + reset toggle
   * - 'clear' → clearSlot + reset toggle
   * - 'none'  → load if slot is filled (otherwise show hint)
   */
  triggerSlot(slot: number): void {
    if (this.state !== GS.DEPLOY) return;
    if (this.slotAction === 'save') {
      this.saveToSlot(slot);
      this.slotAction = 'none';
    } else if (this.slotAction === 'clear') {
      this.clearSlot(slot);
      this.slotAction = 'none';
    } else {
      this.loadFromSlot(slot);
    }
  }

  /** Toggle the save/clear slot action indicator. */
  toggleSlotAction(action: 'save' | 'clear'): void {
    if (this.state !== GS.DEPLOY) return;
    this.slotAction = this.slotAction === action ? 'none' : action;
  }

  /** Snapshot the 3 slots (for UI) — reads from cache. Null entries = empty slots. */
  listSlots(): (ArmyData.ArmyData | null)[] {
    return this._slotsCache;
  }

  private _refreshSlotsCache(): void {
    this._slotsCache = ArmyData.listSlots();
  }

  /** Flash a short status label on screen as feedback for save/load actions. */
  private _slotToast(text: string, color: string): void {
    this.fx.push({
      type: 'floatText',
      x: W / 2,
      y: OYB + CB * 3.5,
      text,
      color,
      alpha: 2.0,
      timer: 1.4,
      size: 15,
    });
  }

  /**
   * Transition from Deployment to Battle — takes units from deployment.
   *
   * Phase 3c Step 5: now async. For the local adapter this resolves
   * synchronously. For the network adapter we await the server ack
   * before starting the first round — otherwise a swap issued in the
   * first 100ms could race ahead of initBattle on the wire.
   */
  async startBattle(): Promise<void> {
    if (!this.deployment.canConfirm()) return;
    if (this.initPending) return; // guard against double-click
    // Story Mode — block the start until any pending deploy-phase
    // constraints (expectPlace / expectPath / expectPathToggle) are
    // satisfied. Without this, the pre-placed Infantry + Archer make
    // `canConfirm()` return true even before the player places the
    // tutorial Cavalry, letting them skip the deploy lesson entirely
    // and corrupt the script position.
    if (this.gameMode === 'story' && this.storyController) {
      if (!this.storyController.isStartBattleAllowed()) return;
      this.storyController.onStartBattle();
    }
    resetId();

    // Phase 3c Step 6: seed lifecycle.
    // Pick a master seed once per battle, then derive independent sub-streams
    // from it (one each for deployment, player's gem board, and AI's gem
    // board). If we ever want to reproduce a session, all we need is the
    // master seed below — everything downstream is deterministic.
    this.masterSeed = randomSeed();
    const master = new SeededRng(this.masterSeed);
    const deploySeed = master.nextUint();
    const playerBoardSeed = master.nextUint();
    const aiBoardSeed = master.nextUint();
    // Phase 3e: stash playerBoardSeed so initBattle can ship it to the server,
    // which will build a Match3Mirror from it for anti-cheat replay.
    this.playerBoardSeed = playerBoardSeed;
    console.log(
      '[game] seed master=' + this.masterSeed +
      ' deploy=' + deploySeed +
      ' playerBoard=' + playerBoardSeed +
      ' aiBoard=' + aiBoardSeed,
    );

    // Session 9 — snapshot leader choices into Game so SCHED / HUD /
    // EXEC paths don't have to keep poking at deployment.leader.
    //   Player: always from deployment.leader (the picker in DEPLOY).
    //   AI / opponent:
    //     - single : from deployment.aiLeader (player picks both in DEPLOY).
    //     - PvP    : DEFAULT for now (TODO: lobby leader picker + push).
    //     - story / casual : DEFAULT (scripts could override later).
    this.playerLeader = this.deployment.leader || DEFAULT_LEADER_ID;
    this.aiLeader = this.gameMode === 'single'
      ? (this.deployment.aiLeader || DEFAULT_LEADER_ID)
      : DEFAULT_LEADER_ID;
    const playerLeaderDef = getLeader(this.playerLeader);
    const aiLeaderDef = getLeader(this.aiLeader);

    // Rebuild boards with seeded RNGs so initial fill + gravity drops are
    // reproducible. Re-wire the match-callbacks since we threw away the
    // originals created in the constructor.
    // Each board gets its OWNER's leader gemElements so the player sees
    // their leader's color palette on their own board (and the AI's
    // mirror board uses the AI leader's palette).
    this.board = new M3Board(new SeededRng(playerBoardSeed), playerLeaderDef.gemElements);
    this.aiBoard = new M3Board(new SeededRng(aiBoardSeed), aiLeaderDef.gemElements);
    this.board._onMatch = (e) => this._onPlayerMatch(e);
    this.board._onChainComplete = (e) => this._onPlayerChainComplete(e);
    this.aiBoard._onMatch = (e) => this._onAIMatch(e);
    this.aiBoard._onChainComplete = (e) => this._onAIChainComplete(e);

    // Build the unit roster.
    //   Player side: always via `exportPlayerUnits(myTeam, viewFlipped)`.
    //                Single source of truth across modes.
    //   AI / opponent side: mode-specific.
    //     - single : `generateAiUnits(rules.composition, rng)` so the
    //                AI mirrors the player's composition cap (single =
    //                offline PvP variant).
    //     - story / casual : convert the level/chapter's preset AI
    //                DeployedUnit[] (stashed in `_presetAiUnits` at
    //                startDeployment / startCasualLevel) into BattleUnit[].
    //     - PvP    : skip — server merges with the opponent's roster
    //                on battleReady push.
    const playerUnits = this.deployment.exportPlayerUnits(this.myTeam, this.viewFlipped);
    let aiUnits: BattleUnit[] = [];
    if (this.gameMode === 'single' && this.deployment.rules.kind === 'manual') {
      aiUnits = generateAiUnits(this.deployment.rules.composition, new SeededRng(deploySeed));
    } else if (
      (this.gameMode === 'story' || this.gameMode === 'casual')
      && this._presetAiUnits
    ) {
      for (const u of this._presetAiUnits) {
        const cfg = UCFG[u.type];
        aiUnits.push(mkUnit(u.type, 1, u.pos.x, u.pos.y, cfg.hp, u.path.map((p) => ({ ...p }))));
      }
    }
    this.units = [...playerUnits, ...aiUnits];
    this.bases = BASES.map(b => mkBase(b.team, b.tiles, b.hp));
    this.round = 0;
    this.fx = [];
    this.winner = -1;
    this.events = [];

    // Phase 3d Step 4: PvP keeps the adapter created back in
    // startCreateRoom / startJoinRoom. Single / Story / Casual create a
    // fresh LocalServerAdapter here.
    //
    // Session 11 follow-up #2 — removed the legacy `(netMode && single)
    // → NetworkServerAdapter` dev override. Reasoning:
    //   - Test Mode (= single) is explicitly the "local-vs-AI sandbox"
    //     per the menu UI copy. Sending it through the network adapter
    //     is never what the user wants.
    //   - The override caused stuck "Initialising battle" / "Connecting
    //     to server" overlays whenever the WS server was unreachable,
    //     both on Android packaged shell (netMode auto-on from
    //     `isPackagedShell()`) AND on web dev with `?net=1` in the URL.
    //   - Devs who want to test the network code path can play PvP via
    //     Create Room / Join Room — those still use NetworkServerAdapter
    //     (and pre-create it earlier in the lobby flow so connection
    //     errors surface in a more obvious place).
    //
    // After this change, Test Mode is fully offline regardless of
    // netMode / URL params / packaged shell status. PvP and lobby flows
    // are untouched.
    if (this._isLocalMode()) {
      this.adapter = new LocalServerAdapter();
    } else {
      // Phase 3d Step 4: arm the waiting flags BEFORE awaiting initBattle.
      // The server's battleReady push can race with the initBattle rpcResult
      // (server fires push first, then ack). If we don't arm these now, a
      // fast push would be ignored and we'd sit in DEPLOY forever.
      this.awaitingBattleReady = true;
      this.waitingForOpponent = true;
    }

    this.initPending = true;
    this.adapterError = null;
    // Label reflects actual adapter: NetworkServerAdapter (PvP only —
    // single is always Local after Session 11 follow-up #2) needs a WS
    // connection → show "Connecting to server" so a hang at this stage
    // clearly points at the network. LocalServerAdapter is sync + offline
    // → "Initialising battle" is honest.
    this.loadingLabel = this.adapter instanceof NetworkServerAdapter
      ? 'Connecting to server'
      : 'Initialising battle';
    // Session 6c follow-up: enforce a minimum loading-overlay duration
    // so single-player (LocalServerAdapter resolves synchronously) still
    // sees the spinner. ~700ms feels long enough to register but short
    // enough not to annoy.
    const MIN_LOADING_MS = 700;
    const minLoadingDelay = new Promise<void>((r) => setTimeout(r, MIN_LOADING_MS));
    try {
      // Session 9 — build the per-team leader id array in WORLD-team
      // order (idx 0 = team 0, idx 1 = team 1). For the PvP guest
      // (myTeam = 1) the local player's leader belongs at index 1.
      const leaderByTeam: [LeaderId, LeaderId] = this.myTeam === 0
        ? [this.playerLeader, this.aiLeader]
        : [this.aiLeader, this.playerLeader];
      await Promise.all([
        this.adapter.initBattle(
          this.units,
          this.bases,
          this.masterSeed,
          this.playerBoardSeed,
          this.terrainSeed,
          leaderByTeam,
        ),
        minLoadingDelay,
      ]);
    } catch (err) {
      this.adapterError = `initBattle failed: ${(err as Error).message}`;
      console.error('[game]', this.adapterError);
      this.initPending = false;
      this.loadingLabel = null;
      return;
    }
    this.initPending = false;
    this.loadingLabel = null;

    // Casual mode — override the seed-generated terrain with the level's
    // explicit hand-authored grid. Must happen AFTER initBattle (which
    // populates `state.terrain` from the seed) but BEFORE _startRound
    // (which kicks off MATCH3). LocalServerAdapter only — casual mode
    // is local-only by design (gameMode-gated in _isLocalMode).
    if (this.gameMode === 'casual' && this.casualLevel) {
      const grid = buildTerrainGrid(this.casualLevel);
      (this.adapter as LocalServerAdapter).overrideTerrain(grid);
      // Update the client-side copy too so Renderer + Game.terrainAt
      // see the same grid the server is authoritating against.
      this.terrain = grid.map((row) => row.slice());
      this.terrainSeed = 0;
    }

    if (this._isLocalMode()) {
      this._startRound();
    } else {
      // If the battleReady push already arrived during await, the handler
      // has already cleared awaitingBattleReady and called _startRound —
      // don't clobber state in that case.
      if (this.awaitingBattleReady) {
        this.lobbyMessage = 'Deployment sent. Waiting for opponent...';
        this.state = GS.LOBBY;
      }
    }
  }

  /** True for any mode that uses the local in-process adapter (single +
   *  story + casual). Used to gate PvP-only RPC flows and timer countdowns. */
  private _isLocalMode(): boolean {
    return this.gameMode === 'single'
      || this.gameMode === 'story'
      || this.gameMode === 'casual';
  }

  private _startRound(): void {
    this.round++;
    if (this.round > this.maxRounds) { this._endGame(); return; }
    this.state = GS.MATCH3;
    // Session 6c follow-up: longer transition + clearer label when entering
    // the MATCH3 phase from EXEC ("進入魔法獲取" — gem-collection phase).
    // Round 1 keeps the simple Round label since there's no prior phase.
    this.transitionTimer = this.round === 1 ? 0.8 : 1.4;
    this.transitionLabel = this.round === 1
      ? `Round ${this.round}`
      : `進入魔法獲取 (Round ${this.round})`;
    this.pRes = {};
    this.aRes = {};
    this.pResDisplay = {};
    this.aResDisplay = {};
    // Phase 3e: clear last round's swap log; new round starts a fresh
    // record for server replay validation at MATCH3 → SCHED boundary.
    this.swapLog = [];
    this.timer = MATCH3_TIMER_SECS;
    this.pSpells = [null, null, null];
    this.aSpells = [null, null, null];
    this.spellSlot = -1;
    this.schedStep = 'slot';
    this.targeting = false;
    this.bottomTab = 'match3';
    this.pendingSched = false;
    this.schedEnterDelay = 0;
    this.schedEnterTriggered = false;
    this.phaseBanner = null;
    // Story Mode — if the script has queued a preset board (e.g. the
    // first tutorial board for round 1), drop it onto the player's
    // M3Board now so the forced swap sees the intended layout.
    if (this.gameMode === 'story' && this.storyController) {
      this.storyController.notifyPhaseEntered('match3');
      const pb = this.storyController.consumePendingBoard();
      if (pb) this.board.loadFromPreset(pb.grid);
    }
  }

  /**
   * Step-level visuals DURING a cascade. The per-step combo multiplier
   * isn't known yet (depends on chain's final depth), so we ONLY show
   * feedback that's determined by the step itself: group rings, NICE!/
   * BIG! labels, simul tip, and a small "C2/C3/..." step badge. The
   * actual "+N" score float fires at chain end via `_onPlayerChainComplete`.
   */
  private _onPlayerMatch(event: ChainStepEvent): void {
    for (const c of event.components) {
      this._spawnGroupSizeBurst(c);
    }
    if (event.isSimulStep) this._spawnSimulBonusTip(event.components);
    if (event.comboDepth >= 2) this._spawnChainStepIndicator(event.comboDepth);
  }

  /**
   * Chain end — apply the retroactive scoring (already resolved in M3Board)
   * to `pRes`, fire the big finale animations (per-element "+TotalPts"
   * flying to HUD, chain summary banner, tier-up bursts), and push to
   * the server in local-mode.
   */
  private _onPlayerChainComplete(event: ChainCompleteEvent): void {
    for (const [keyStr, points] of Object.entries(event.pointsByElement)) {
      if (points <= 0) continue;
      const el = Number(keyStr);
      const prevPoints = this.pRes[keyStr] || 0;
      const prevLevel = pointsToLevel(prevPoints);
      const nextPoints = prevPoints + points;
      this.pRes[keyStr] = nextPoints;
      const nextLevel = pointsToLevel(nextPoints);
      const tierUp = nextLevel > prevLevel;
      this.resFlash[keyStr] = tierUp ? 1.5 : 0.55;
      this._spawnChainTotalFx(el, points, tierUp, event);
      if (tierUp) this._spawnLevelBurst(el, nextLevel);
    }
    if (event.effChainDepth >= 2) this._spawnChainSummaryBanner(event);
    if (this._isLocalMode() && this.adapter) {
      // Local adapter: zero-latency, push the new running totals.
      // (PvP sends final tally + swapLog at MATCH3 → SCHED, see _enterSched.)
      void this.adapter.commitMatchResults(this.myTeam, this.pRes).catch((err) => {
        console.warn('[game] commitMatchResults(' + this.myTeam + ') failed:', (err as Error).message);
      });
    }
  }

  /** AI step — no FX (opponent moves shouldn't clutter the player's screen). */
  private _onAIMatch(_event: ChainStepEvent): void {
    // Intentionally empty.
  }

  /** AI chain end — commit the resolved scoring to `aRes`. */
  private _onAIChainComplete(event: ChainCompleteEvent): void {
    if (this.gameMode !== 'single') return;
    for (const [keyStr, points] of Object.entries(event.pointsByElement)) {
      if (points <= 0) continue;
      this.aRes[keyStr] = (this.aRes[keyStr] || 0) + points;
    }
    if (this.adapter) {
      void this.adapter.commitMatchResults(1, this.aRes).catch((err) => {
        console.warn('[game] commitMatchResults(1) failed:', (err as Error).message);
      });
    }
  }

  /**
   * Per-group elimination FX (fires per match group on every cascade step).
   *
   * Layered by group size so a basic 3-match still gets element-color
   * feedback (without it the cascade looks dead until a 4+ shows up), while
   * 5/6+ groups remain the show-stoppers with labels + screen flash.
   *
   * Tiers:
   *   3      — small ring + 2 sparks (subtle "something happened" beat)
   *   4      — medium ring + 4 sparks
   *   5      — bigger ring + 8 sparks + "NICE!" + element flash α=0.12
   *   6+     — biggest ring + 12 sparks + "BIG!"  + element flash α=0.22
   */
  private _spawnGroupSizeBurst(group: { el: number; count: number; centerR: number; centerC: number }): void {
    const cx = OXM + group.centerC * CM + CM / 2;
    const cy = OYM + group.centerR * CM + CM / 2;
    const elemColor = ECOL[ELEM[group.el]] || '#fff';

    // Tier the spectacle by group size.
    let ringRadius: number, particleCount: number, ringAlpha: number;
    let label: string | null = null;
    let labelSize = 0;
    let flashAlpha = 0;
    if (group.count >= 6) {
      ringRadius = CM * 1.4; particleCount = 12; ringAlpha = 1.0;
      label = 'BIG!'; labelSize = 22; flashAlpha = 0.22;
    } else if (group.count >= 5) {
      ringRadius = CM * 1.0; particleCount = 8; ringAlpha = 1.0;
      label = 'NICE!'; labelSize = 18; flashAlpha = 0.12;
    } else if (group.count === 4) {
      ringRadius = CM * 0.7; particleCount = 4; ringAlpha = 0.95;
    } else {
      // count === 3 — minimum but present so the cascade still "fizzes".
      ringRadius = CM * 0.5; particleCount = 2; ringAlpha = 0.75;
    }

    // Expanding ring — initial radius scales with the tier; growth speed
    // is the global ring rate so other FX timing stays consistent.
    this.fx.push({
      type: 'ring',
      x: cx, y: cy,
      color: elemColor,
      alpha: ringAlpha,
      timer: group.count >= 4 ? 0.65 : 0.45,
      radius: ringRadius * 0.55,
    });
    // Outward particle spray.
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.3;
      const speed = group.count >= 4
        ? 100 + Math.random() * 70
        : 70 + Math.random() * 40;  // 3-match: smaller spread
      this.fx.push({
        type: 'particle',
        x: cx, y: cy,
        dx: Math.cos(angle) * speed,
        dy: Math.sin(angle) * speed - 30,  // slight upward bias
        color: elemColor,
        alpha: 1.0,
        timer: 0.5 + Math.random() * 0.2,
        size: group.count >= 4 ? 3 : 2,
      });
    }
    // Optional label (NICE! / BIG!) above the group.
    if (label) {
      this.fx.push({
        type: 'floatText',
        x: cx, y: cy - CM * 0.5,
        text: label,
        color: elemColor,
        alpha: 1.3,
        timer: 0.9,
        size: labelSize,
      });
    }
    // Subtle screen flash for the truly big ones (5+).
    if (flashAlpha > 0) {
      this.fx.push({
        type: 'flash',
        x: 0, y: 0,
        color: elemColor,
        alpha: flashAlpha,
        timer: 0.35,
      });
    }
  }

  /**
   * Simul-bonus tip — small hint animation when a cascade step has ≥ 2
   * separate match components (the simultaneous-multi-group bonus,
   * §2.4a method B). Fires DURING the cascade step (immediately known
   * from the step's component count, doesn't need the final chain mult).
   *
   * Visual: floatText "SIMUL ×N!" at the average centre of all groups
   * in white-gold, plus a small white ring for a subtle pulse.
   */
  private _spawnSimulBonusTip(components: ReadonlyArray<MatchComponent>): void {
    if (components.length < 2) return;
    let sumR = 0, sumC = 0;
    for (const c of components) { sumR += c.centerR; sumC += c.centerC; }
    const avgR = sumR / components.length;
    const avgC = sumC / components.length;
    const cx = OXM + avgC * CM + CM / 2;
    const cy = OYM + avgR * CM + CM / 2;
    this.fx.push({
      type: 'floatText',
      x: cx,
      y: cy - CM * 0.25,
      text: `SIMUL ×${components.length}!`,
      color: '#ffe680',
      alpha: 1.4,
      timer: 1.0,
      size: 13,
    });
    this.fx.push({
      type: 'ring',
      x: cx, y: cy,
      color: '#ffffff',
      alpha: 0.8,
      timer: 0.5,
      radius: 6,
    });
  }

  /**
   * Chain step indicator — fires DURING the cascade for steps ≥ 2 to say
   * "the chain is building" without committing to a mult number yet.
   *
   * Two parts:
   *   1. Top-of-board badge (small text, fades in ~0.6s) — keeps the
   *      step counter readable; offset slightly per step so consecutive
   *      C2/C3/C4 indicators don't clobber each other.
   *   2. Central particle burst + ring + (optional) screen flash —
   *      makes the cascade FEEL alive. Particle count and flash strength
   *      scale with depth so the player viscerally feels "this is escalating":
   *        C2 → 6 sparks + ring        (no flash)
   *        C3 → 10 sparks + ring       + flash α=0.10
   *        C4+ → 16 sparks + big ring  + flash α=0.18
   *
   * Particles use chain-color (yellow/orange/red-gold), distinct from the
   * per-group element-colored rings so the player can tell apart "this
   * group matched" from "the chain just escalated".
   */
  private _spawnChainStepIndicator(depth: number): void {
    const badgeX = OXM + (M3_COLS * CM) / 2;
    const badgeY = OYM + 8 + ((depth - 2) % 3) * 10;
    const centerX = OXM + (M3_COLS * CM) / 2;
    const centerY = OYM + (M3_ROWS * CM) / 2;

    let color: string, size: number;
    let particleCount: number, particleSize: number;
    let ringRadius: number, flashAlpha: number;
    if (depth >= 4) {
      color = '#ff5544'; size = 18;
      particleCount = 16; particleSize = 4;
      ringRadius = CM * 0.9; flashAlpha = 0.18;
    } else if (depth === 3) {
      color = '#ff9933'; size = 16;
      particleCount = 10; particleSize = 3;
      ringRadius = CM * 0.7; flashAlpha = 0.10;
    } else {
      // depth === 2
      color = '#ffcc44'; size = 14;
      particleCount = 6; particleSize = 3;
      ringRadius = CM * 0.5; flashAlpha = 0;
    }

    // 1. Badge text at top of M3 area.
    this.fx.push({
      type: 'floatText',
      x: badgeX, y: badgeY,
      text: `C${depth}!`,
      color,
      alpha: 1.6,
      timer: 0.6,
      size,
    });
    // 2a. Central ring — punchy "chain just escalated" beat.
    this.fx.push({
      type: 'ring',
      x: centerX, y: centerY,
      color,
      alpha: 0.85,
      timer: 0.55,
      radius: ringRadius * 0.4,
    });
    // 2b. Outward particle burst from board center.
    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.2;
      const speed = 110 + Math.random() * 60;
      this.fx.push({
        type: 'particle',
        x: centerX, y: centerY,
        dx: Math.cos(angle) * speed,
        dy: Math.sin(angle) * speed - 20,
        color,
        alpha: 1.0,
        timer: 0.5 + Math.random() * 0.15,
        size: particleSize,
      });
    }
    // 2c. Screen flash for C3+ (gold tint reinforces the chain color).
    if (flashAlpha > 0) {
      this.fx.push({
        type: 'flash',
        x: 0, y: 0,
        color, alpha: flashAlpha,
        timer: 0.3,
      });
    }
  }

  /**
   * Big "+TotalPoints" fly-text fired AT CHAIN END (one per element that
   * scored anything). Travels from the M3 board area toward the HUD
   * resource icon for that element. Bigger / brighter than the old
   * per-step m3Score because this is the "moment of truth" reveal of
   * the chain's full reward.
   *
   * Tier-up bumps the size and adds a small golden inner ring at origin
   * to emphasise the level jump.
   */
  private _spawnChainTotalFx(el: number, totalPoints: number, tierUp: boolean, event: ChainCompleteEvent): void {
    if (totalPoints <= 0) return;
    // Origin: slightly offset per element so multi-element finales don't
    // pile up. Centre of M3 area + small per-el offset.
    const baseX = OXM + (M3_COLS * CM) / 2;
    const baseY = OYM + (M3_ROWS * CM) / 2;
    // Session 9 — slot index = position inside the leader's spellElements
    // array (0..4), not (el - 1). Without this BBB's WindFriendly (6) /
    // WindHostile (7) would fly off the right side of the HUD.
    const ldrEls = getLeader(this.playerLeader).spellElements;
    const slotIdx = Math.max(0, ldrEls.indexOf(el));
    const offIdx = (slotIdx % 5) - 2;          // -2..+2
    const startX = baseX + offIdx * (CM * 0.55);
    const startY = baseY + offIdx * (CM * 0.35);
    const targetX = W / 2 - 90 + slotIdx * 40;
    const targetY = ZHY + (ZMY - ZHY) * 0.9;
    const elemColor = ECOL[ELEM[el]] || '#fff';

    // Bonus indicator suffix when the chain was deep enough to be exciting.
    const chainTag = event.effChainDepth >= 2 ? ` ×${event.effChainDepth}!` : '';
    this.fx.push({
      type: 'm3Score',
      x: startX, y: startY,
      x1: startX, y1: startY,
      x2: targetX, y2: targetY,
      text: `+${totalPoints}${chainTag}`,
      color: elemColor,
      alpha: 1.0,
      timer: tierUp ? 1.6 : 1.2,
      size: tierUp ? 24 : (event.effChainDepth >= 3 ? 22 : 18),
    });
    // Inner ring at origin to "punch" the moment.
    this.fx.push({
      type: 'ring',
      x: startX, y: startY,
      color: tierUp ? '#ffd24a' : elemColor,
      alpha: 1.0,
      timer: 0.6,
      radius: 8,
    });
  }

  /**
   * Chain summary banner — single big central text at CHAIN END when
   * the effective chain depth ≥ 2. Replaces the per-step combo banner
   * that used to fire mid-cascade. Color/size scales with depth.
   */
  private _spawnChainSummaryBanner(event: ChainCompleteEvent): void {
    const cx = OXM + (M3_COLS * CM) / 2;
    const cy = OYM + (M3_ROWS * CM) / 2;
    const N = event.effChainDepth;
    let text: string, color: string, size: number, flashAlpha: number;
    if (N >= 5) {
      text = `MEGA CHAIN ×${N}!`; color = '#ffd24a'; size = 26; flashAlpha = 0.30;
    } else if (N === 4) {
      text = `BIG CHAIN ×4!`; color = '#ff7a33'; size = 23; flashAlpha = 0.20;
    } else if (N === 3) {
      text = `CHAIN ×3!`; color = '#ff9933'; size = 20; flashAlpha = 0.12;
    } else {
      // N === 2
      text = `CHAIN ×2!`; color = '#ffaa44'; size = 17; flashAlpha = 0;
    }
    this.fx.push({
      type: 'comboBanner',
      x: cx, y: cy,
      text,
      color,
      alpha: 1.0,
      timer: 1.2,
      size,
      scale: 0.4,
    });
    if (flashAlpha > 0) {
      this.fx.push({
        type: 'flash',
        x: 0, y: 0,
        color, alpha: flashAlpha,
        timer: 0.4,
      });
    }
  }

  /**
   * Level-up burst at the HUD element bar — fired on tier crossings.
   * Two-part effect: a ring + 6 outward particles for impact, plus a
   * "LV N!" floatText rising above the HUD so the player can read the
   * new tier number directly. Max-tier (Lv 7) adds an extra golden ring.
   */
  private _spawnLevelBurst(el: number, newLevel: number): void {
    // Session 9 — slot index from the leader's spellElements (see note
    // in `_spawnChainTotalFx`).
    const ldrEls = getLeader(this.playerLeader).spellElements;
    const slotIdx = Math.max(0, ldrEls.indexOf(el));
    const hx = W / 2 - 90 + slotIdx * 40;
    const hy = ZHY + (ZMY - ZHY) * 0.78;  // label row
    const elemColor = ECOL[ELEM[el]] || '#fff';

    // Primary ring at HUD position.
    this.fx.push({
      type: 'ring',
      x: hx, y: hy,
      color: elemColor,
      alpha: 1.0,
      timer: 0.65,
      radius: 6,
    });
    // 6 outward sparks.
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 * i) / 6;
      const speed = 70 + Math.random() * 30;
      this.fx.push({
        type: 'particle',
        x: hx, y: hy,
        dx: Math.cos(angle) * speed,
        dy: Math.sin(angle) * speed - 20,
        color: elemColor,
        alpha: 1.0,
        timer: 0.55,
        size: 3,
      });
    }
    // "LV N!" floatText rising above the HUD bar.
    this.fx.push({
      type: 'floatText',
      x: hx, y: hy - 8,
      text: `LV ${newLevel}!`,
      color: elemColor,
      alpha: 1.6,
      timer: 1.2,
      size: newLevel >= 7 ? 18 : 14,
    });
    // Max tier — extra golden ring + small flash.
    if (newLevel >= 7) {
      this.fx.push({
        type: 'ring',
        x: hx, y: hy,
        color: '#ffd24a',
        alpha: 0.9,
        timer: 0.9,
        radius: 4,
      });
      this.fx.push({
        type: 'flash',
        x: 0, y: 0,
        color: '#ffd24a',
        alpha: 0.20,
        timer: 0.45,
      });
    }
  }

  update(dt: number): void {
    anim.update(dt);
    this.selPulse += dt * 4;
    // Tick transition
    if (this.transitionTimer > 0) this.transitionTimer -= dt;
    // Tick resource flash timers
    for (const k in this.resFlash) {
      this.resFlash[k] -= dt;
      if (this.resFlash[k] <= 0) delete this.resFlash[k];
    }
    // Smooth HUD point counters — pResDisplay/aResDisplay catch up to
    // pRes/aRes with an exponential ease (rate ~7/sec gives ~0.3s settle
    // time). The Renderer reads from the *Display variants so the bar
    // fills smoothly and the level number ticks up at the exact frame
    // the displayed value crosses a tier threshold.
    const easeRate = Math.min(1, dt * 7);
    for (const k in this.pRes) {
      const target = this.pRes[k] || 0;
      const cur = this.pResDisplay[k] || 0;
      const next = cur + (target - cur) * easeRate;
      // Snap when within 0.5 of the target so we converge cleanly.
      this.pResDisplay[k] = Math.abs(target - next) < 0.5 ? target : next;
    }
    for (const k in this.aRes) {
      const target = this.aRes[k] || 0;
      const cur = this.aResDisplay[k] || 0;
      const next = cur + (target - cur) * easeRate;
      this.aResDisplay[k] = Math.abs(target - next) < 0.5 ? target : next;
    }
    // Update FX
    for (let i = this.fx.length - 1; i >= 0; i--) {
      const f = this.fx[i];
      f.timer -= dt;
      if (f.type === 'floatText') { f.y! -= 25 * dt; f.alpha -= dt * 0.7; }
      if (f.type === 'm3Score') {
        // Points & Tiers: "+N" travels from gem centre toward the HUD
        // element bar with an exponential ease, fading as it goes.
        // x2/y2 are the target. Using an instantaneous ease (not a
        // pre-baked tween) so it stays smooth across resize.
        const k = Math.min(1, dt * 3.2);
        f.x! += (f.x2! - f.x!) * k;
        f.y! += (f.y2! - f.y!) * k;
        f.alpha -= dt * 1.1;
      }
      if (f.type === 'ring') { f.radius! += 60 * dt; f.alpha -= dt * 1.2; }
      if (f.type === 'slash') { f.alpha -= dt * 1.5; }
      if (f.type === 'moveTrail') { f.alpha -= dt * 2.0; }
      if (f.type === 'particle') { f.x! += f.dx! * dt; f.y! += f.dy! * dt; f.dy! += 80 * dt; f.alpha -= dt * 1.5; }
      if (f.type === 'comboBanner') {
        // Scale-up bounce: 0.4 → 1.2 → fade. Fully bouncy: scale grows
        // toward 1.2 with overshoot, alpha fades after ~0.4s.
        const target = 1.2;
        f.scale = (f.scale || 0.4) + (target - (f.scale || 0.4)) * Math.min(1, dt * 8);
        // Hold alpha for first 0.5s, then fade.
        if (f.timer < 0.5) f.alpha -= dt * 2.2;
      }
      if (f.type === 'flash') {
        // Full-screen colour overlay — quick decay so the screen doesn't
        // stay tinted past the moment of impact.
        f.alpha -= dt * 1.2;
        if (f.alpha < 0) f.alpha = 0;
      }
      if (f.type === 'deathX') { f.alpha -= dt * 0.8; f.scale = (f.scale || 1) + dt * 0.5; }
      if (f.type === 'moveArrow') { f.alpha -= dt * 1.8; }
      if (f.timer <= 0) this.fx.splice(i, 1);
    }

    // Surrender state is MATCH3-only — if we transitioned out (to SCHED
    // / EXEC / OVER) while the player was mid-hold or saw the confirm
    // modal, drop it silently.
    if (this.state !== GS.MATCH3 && (this.surrenderHolding || this.surrenderConfirmActive)) {
      this.surrenderHolding = false;
      this.surrenderHoldTime = 0;
      this.surrenderConfirmActive = false;
    }

    switch (this.state) {
      case GS.MENU:
        break;
      case GS.STORY_SELECT:
        break;
      case GS.CASUAL_SELECT:
        break;
      case GS.DEPLOY:
        if (this.gameMode === 'story' && this.storyController
            && !this.storyController.shouldPauseGame()
            && !this.initPending) {
          // Story Mode — pull a deferred presetDeployment (script may
          // produce one after the player dismisses an early dialogue).
          // startDeployment() already consumed any preset that existed
          // at DEPLOY entry, but subsequent script steps can queue new
          // ones until the script moves on to a MATCH3-phase step.
          const preset = this.storyController.consumePendingDeployment();
          if (preset) {
            this.deployment.loadFromUnits(preset.player.map((u) => ({
              type: u.type,
              pos: { x: u.pos.x, y: u.pos.y },
              path: u.path.map((p) => ({ x: p.x, y: p.y })),
            })));
            this._presetAiUnits = preset.ai.map((u) => ({
              type: u.type,
              pos: { x: u.pos.x, y: u.pos.y },
              path: u.path.map((p) => ({ x: p.x, y: p.y })),
            }));
          }
          // When the script wants the player to draw a path, pre-select
          // the target unit so the player only has to tap the path cells
          // (selecting the right unit isn't part of the lesson). Path
          // Mode itself is NOT auto-toggled — the player goes through
          // the `expectPathToggle` step which gates the "Draw Paths"
          // button so the toggle UI is part of the tutorial.
          const ep = this.storyController.expectedPath;
          if (ep) {
            const idx = this.deployment.unitAt(ep.unitPos.x, ep.unitPos.y);
            if (idx >= 0 && this.deployment.selectedUnit !== idx) {
              this.deployment.selectedUnit = idx;
            }
          }
          // When the script wants the player to PRESS Start Battle
          // (expectStartBattle), don't auto-start — let the player tap.
          // Otherwise auto-start as soon as MATCH3-phase steps queue up.
          if (this.deployment.canConfirm() && this.storyController.shouldStartBattle()) {
            void this.startBattle();
          }
        }
        break;
      case GS.MATCH3:
        // PvP only — single + story have no timer.
        if (!this._isLocalMode()) this.timer -= dt;
        this._aiMatch3(dt);
        // Surrender long-press tick — only advances while player is
        // actively pressing the button (Input keeps `surrenderHolding`
        // in sync). At 5s elapsed, pop the confirm modal.
        if (this.surrenderHolding && !this.surrenderConfirmActive) {
          this.surrenderHoldTime += dt;
          if (this.surrenderHoldTime >= Game.SURRENDER_HOLD_SECS) {
            this.surrenderConfirmActive = true;
            this.surrenderHolding = false;
            this.surrenderHoldTime = 0;
          }
        }
        // Story: poll for match-settle so StoryController can advance past
        // a forced swap (show after-lines, then load next preset board).
        if (this.gameMode === 'story' && this._storySettlePending
            && !this.board.locked && !anim.busy && this.storyController) {
          this._storySettlePending = false;
          this.storyController.onMatchSettle();
        }
        // Always check for a pending preset board — it may have been queued
        // after the most recent dialogue dismissal (after-lines for the
        // last forced swap → next presetBoard step).
        if (this.gameMode === 'story' && this.storyController
            && !this.board.locked && !anim.busy
            && !this.storyController.shouldPauseGame()) {
          const pb = this.storyController.consumePendingBoard();
          if (pb) this.board.loadFromPreset(pb.grid);
        }
        // Session 6c item 5: 30s OR 10 swaps, whichever comes first.
        if ((!this._isLocalMode() && this.timer <= 0) || this.swapsExhausted()) this.pendingSched = true;
        // Session 6c follow-up: wait for the board to settle (no flying
        // gems / no in-flight cascade), then hold ~1.4s with a transition
        // banner so the SCHED handoff has visual breathing room.
        if (this.pendingSched && !this.board.locked) {
          // Story mode: don't transition while a dialogue overlay is active —
          // the player needs to dismiss tutorial dialogue first.
          if (this.storyController?.shouldPauseGame()) break;
          if (!this.schedEnterTriggered) {
            this.schedEnterTriggered = true;
            this.schedEnterDelay = 1.4;
            this.transitionTimer = 1.4;
            this.transitionLabel = '進入魔法部屬';
          } else {
            this.schedEnterDelay -= dt;
            if (this.schedEnterDelay <= 0) this._enterSched();
          }
        }
        break;
      case GS.SCHED:
        if (!this._isLocalMode()) {
          this.timer -= dt;
          if (this.timer <= 0) this._finalizeSched();
        }
        break;
      case GS.EXEC:
        this._updateExec(dt);
        break;
    }
  }

  private _aiMatch3(dt: number): void {
    // Phase 3d Step 3: in PvP modes the opponent's match-3 happens on
    // their own client. We must not simulate it locally (would inject
    // bogus aRes which then drives the auto-AI schedule below).
    // Story Mode: skip because the script overrides the AI schedule
    // deterministically via storyController.aiScheduleFor().
    // Casual Mode: included — same dumb single-player AI runs match-3
    // locally so its spell resource pool fills naturally and the SCHED
    // auto-picker can deliver something interesting at EXEC.
    if (this.gameMode !== 'single' && this.gameMode !== 'casual') return;

    this._aiTimer += dt;
    if (this._aiTimer < 1.0) return;
    this._aiTimer = 0;

    // Smart AI: scan all possible swaps, pick the best one (most gems matched)
    const g = this.aiBoard.grid;
    let bestSwap: { r1: number; c1: number; r2: number; c2: number; score: number } | null = null;

    for (let r = 0; r < M3_ROWS; r++) {
      for (let c = 0; c < M3_COLS; c++) {
        const dirs = [[0, 1], [1, 0]]; // right, down
        for (const [dr, dc] of dirs) {
          const r2 = r + dr, c2 = c + dc;
          if (r2 >= M3_ROWS || c2 >= M3_COLS) continue;
          // Tentative swap
          const a = g[r][c], b = g[r2][c2];
          g[r][c] = b; g[r2][c2] = a;
          if (a) { a.r = r2; a.c = c2; }
          if (b) { b.r = r; b.c = c; }
          const matches = this.aiBoard._findMatches();
          // Undo
          g[r][c] = a; g[r2][c2] = b;
          if (a) { a.r = r; a.c = c; }
          if (b) { b.r = r2; b.c = c2; }

          if (matches.size > 0) {
            const score = matches.size;
            if (!bestSwap || score > bestSwap.score) {
              bestSwap = { r1: r, c1: c, r2, c2, score };
            }
          }
        }
      }
    }

    if (bestSwap) {
      // Execute the best swap
      const { r1, c1, r2, c2 } = bestSwap;
      const a = g[r1][c1], b = g[r2][c2];
      g[r1][c1] = b; g[r2][c2] = a;
      if (a) { a.r = r2; a.c = c2; }
      if (b) { b.r = r1; b.c = c1; }

      const matches = this.aiBoard._findMatches();
      // Points & Tiers — same retroactive chain formula as the player
      // side (§2.4a). AI doesn't model gravity-driven cascades on its
      // own grid (it does its own gravity+fill below), so the chain is
      // always 1 raw step. Simul-bonus (method B) still applies: ≥ 2
      // components → effChainDepth bumps from 1 to 2, comboMult = 2.5.
      const aiComponents = findMatchComponents(g, matches);
      const aiSimulBump = aiComponents.length >= 2 ? 1 : 0;
      const aiEffDepth = 1 + aiSimulBump;
      const aiComboMult = chainStepMult(1, aiEffDepth);
      for (const c of aiComponents) {
        const points = chainPointsForGroup(c.count, aiComboMult);
        if (points <= 0) continue;
        this.aRes[String(c.el)] = (this.aRes[String(c.el)] || 0) + points;
      }
      if (this.adapter) {
        void this.adapter.commitMatchResults(1, this.aRes).catch((err) => {
          console.warn('[game] commitMatchResults(1) failed:', (err as Error).message);
        });
      }
      for (const idx of matches) {
        const mr = Math.floor(idx / M3_COLS), mc = idx % M3_COLS;
        g[mr][mc] = null;
      }
      // Gravity + fill
      for (let col = 0; col < M3_COLS; col++) {
        let wr = M3_ROWS - 1;
        for (let row = M3_ROWS - 1; row >= 0; row--) {
          if (g[row][col]) {
            if (row !== wr) { g[wr][col] = g[row][col]; g[wr][col]!.r = wr; g[row][col] = null; }
            wr--;
          }
        }
        // Seed new gems from the aiBoard's RNG so deterministic replay works.
        // BUG FIX (Session 11) — must also pass the AI board's `elements` pool
        // (= AI leader's `gemElements`), otherwise the 5-arg Gem constructor
        // falls back to the contiguous range `M3.minGemElement..maxGemElement`
        // (1..5) and never spawns ELEM ≥ 6. Result: BBB's WindFriendly /
        // WindHostile (ELEM 6/7) and CCC's Grow / Rain (ELEM 8/9) get drained
        // from the initial fill and never refilled, so AI's aRes for those
        // ELEMs flatlines after a few matches → AI's SCHED picker (which
        // filters by leader.spellElements) sees nothing eligible there → AI
        // only ever casts the legacy 5 ELEMs that overlap with the fallback.
        // Player-side M3Board.applyGravityAnimated already passes `elements`
        // correctly (see M3Board.ts line ~453); this manual refill is
        // _aiMatch3's locally-orchestrated mirror of that path and was the
        // only callsite missing the 5th argument.
        for (let row = wr; row >= 0; row--) {
          g[row][col] = new Gem(
            row, col, undefined, this.aiBoard.rng,
            this.aiBoard.elements ?? undefined,
          );
        }
      }
    }
  }

  private _enterSched(): void {
    this.state = GS.SCHED;
    // Story Mode — let the controller resume any `waitForPhase: 'sched'`
    // step that was parked during MATCH3 (e.g. the SCHED-intro dialogue).
    if (this.gameMode === 'story' && this.storyController) {
      this.storyController.notifyPhaseEntered('sched');
    }
    // Session 6c follow-up: the "進入魔法部屬" banner has already played
    // during the schedEnterDelay; just leave the existing transitionTimer
    // running (or skip if it's drained).
    this.schedEnterTriggered = false;
    this.schedEnterDelay = 0;
    this.timer = SCHED_TIMER_SECS;
    this.spellSlot = 0;
    this.schedStep = 'slot';
    this.usedSpellEls = [];
    this.pendingSched = false;
    this.bottomTab = 'sched';
    this._refreshAvailSpells();
    // Phase 3e: PvP sends the round's final stats + swapLog to the server
    // here, on the MATCH3 → SCHED boundary. Server replays the log against
    // its `Match3Mirror` (Step 4a shadow → 4b enforce). Fire-and-forget:
    // the SCHED phase has 10s of human input, easily enough time for the
    // ~80ms round-trip to land before submitSchedule.
    if (!this._isLocalMode() && this.adapter) {
      const swapLog = this.swapLog.slice();
      const stats = { ...this.pRes };
      void this.adapter
        .commitMatchResults(this.myTeam, stats, { swapLog, round: this.round })
        .catch((err) => {
          console.warn(
            '[game] final commitMatchResults failed (' + this.myTeam + '):',
            (err as Error).message,
          );
        });
    }
    // Phase 3d Step 3: in PvP modes the opponent's schedule is sent by
    // their own client. For Step 3 we just leave aSpells as nulls; the
    // single-player + story paths still fill them with the AI plan below.
    if (!this._isLocalMode()) return;
    // Story Mode — chapter may override the AI's schedule deterministically.
    // Top up `aRes` so the AI's chosen powerLevel passes server validation,
    // AND commit the topped-up stats to the server (story mode never runs
    // `_aiMatch3`, so the server's `aiStats` would otherwise be empty and
    // `validateSchedule` would reject any AI spell with powerLevel > 0 —
    // this was the "EXEC plays nothing → goes straight to round 2 MATCH3"
    // bug reported in tutorial testing).
    if (this.gameMode === 'story' && this.storyController) {
      const sched = this.storyController.aiScheduleFor(this.round);
      if (sched && sched.length === 3) {
        const THRESH = [10, 20, 35, 60, 90, 130, 180];
        for (const cmd of sched) {
          if (cmd.spellType === 0 || cmd.powerLevel <= 0) continue;
          const need = THRESH[Math.min(cmd.powerLevel - 1, THRESH.length - 1)];
          const key = String(cmd.spellType);
          if ((this.aRes[key] || 0) < need) this.aRes[key] = need;
        }
        if (this.adapter) {
          void this.adapter.commitMatchResults(1, this.aRes).catch((err) => {
            console.warn('[game/story] commitMatchResults(AI) failed:', (err as Error).message);
          });
        }
        this.aSpells = sched.map((c) => ({ ...c })) as (SpellCommand | null)[];
        return;
      }
    }
    // AI schedule — smart targeting. Points & Tiers: aRes stores raw
    // points per element, so derive powerLevel via pointsToLevel() before
    // populating AvailSpell.lv.
    // Session 9 — restrict to the AI's leader's spellElements (defensive;
    // _aiMatch3 already only fills aRes with the AI leader's gem colors).
    const aiAllowed = new Set<number>(getLeader(this.aiLeader).spellElements);
    const aiAvail: AvailSpell[] = [];
    for (const [t, points] of Object.entries(this.aRes)) {
      if (t === '0') continue;
      const el = parseInt(t);
      if (!aiAllowed.has(el)) continue;
      const lv = pointsToLevel(points);
      if (lv > 0) aiAvail.push({ el, lv });
    }
    // Session 10 follow-up — Fisher-Yates shuffle BEFORE the stable
    // sort so equal-level entries tie-break randomly. Without this,
    // `Object.entries` returns numeric-key strings in ascending order
    // ('1', '2', '5', '6', '7'), so a stable sort by lv-desc always
    // favors lower ELEM ids → AI mostly picks Fire/Water/Lightning
    // when several elements are at similar levels, and BBB's whirlwind
    // (ELEM 6/7) almost never gets used. Shuffling first gives each
    // ELEM ~equal chance when their levels are tied.
    for (let i = aiAvail.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [aiAvail[i], aiAvail[j]] = [aiAvail[j], aiAvail[i]];
    }
    // Sort by power level descending (V8's TimSort is stable since
    // ES2019, so equal-lv entries retain shuffle order).
    aiAvail.sort((a, b) => b.lv - a.lv);
    const aiUsed: number[] = [];
    for (let i = 0; i < 3; i++) {
      const remaining = aiAvail.filter(s => !aiUsed.includes(s.el));
      if (remaining.length > 0) {
        const pick = remaining[0]; // Best available spell
        aiUsed.push(pick.el);
        const sp = SPELLS[ELEM[pick.el]];
        const targets = this.units.filter(u => !u.dead && u.team === 0);
        let tgt: Pos = { x: 3, y: 5 };
        if (targets.length > 0) {
          // Session 10 — sp.type is gone; use sp.scope to pick "aim at
          // enemy strongest" vs "aim at own weakest" without enumerating
          // every effect kind. Whirlwind-hostile (scope='enemy') still
          // lands on the biggest enemy cluster; whirlwind-friendly
          // (scope='own') lands on a fragile ally to pull help in.
          //
          // Session 12 follow-up — `'all'` scope (damage spells with
          // friendly fire) is treated like `'enemy'` for AI targeting:
          // self-bombing is never the right play, so aim at the biggest
          // enemy unit and accept whatever splash hits own units.
          if (sp && (sp.scope === 'enemy' || sp.scope === 'all')) {
            // Target the biggest enemy unit
            targets.sort((a, b) => b.hp - a.hp);
            tgt = { ...targets[0].pos };
          } else {
            // own scope (heal / buff / whirlwind-friendly): target own weakest unit
            const own = this.units.filter(u => !u.dead && u.team === 1);
            if (own.length > 0) {
              own.sort((a, b) => a.hp - b.hp);
              tgt = { ...own[0].pos };
            }
          }
        }
        this.aSpells[i] = { slotIndex: i, spellType: pick.el, powerLevel: pick.lv, targetPos: tgt };
      } else {
        this.aSpells[i] = { slotIndex: i, spellType: 0, powerLevel: 0 };
      }
    }
  }

  _refreshAvailSpells(): void {
    // Points & Tiers: pRes stores raw points; the SCHED UI / validator
    // cares about the derived powerLevel. Filter elements that haven't
    // reached Level 1 yet (< first threshold) — they're not selectable.
    //
    // Session 9 — also gate by leader.spellElements. The board only
    // generates gems for elements in the leader's pool, so foreign-ELEM
    // entries in pRes should never appear; we still defensively filter
    // here so an inconsistent state can't leak a forbidden spell into
    // the picker UI.
    this.availSpells = [];
    const allowed = new Set<number>(getLeader(this.playerLeader).spellElements);
    for (const [t, points] of Object.entries(this.pRes)) {
      const el = parseInt(t);
      if (el <= 0 || this.usedSpellEls.includes(el)) continue;
      if (!allowed.has(el)) continue;
      const lv = pointsToLevel(points);
      if (lv > 0) this.availSpells.push({ el, lv });
    }
  }

  selectSpell(elType: number): void {
    if (this.spellSlot < 0 || this.spellSlot > 2) return;
    // Story Mode — block non-scripted element choices when a constraint
    // is active. Dialogue is also paused-state.
    if (this.storyController) {
      if (this.storyController.shouldPauseGame()) return;
      if (!this.storyController.isSpellElAllowed(this.spellSlot, elType)) return;
    }
    this.selSpellType = elType;
    const sp = SPELLS[ELEM[elType]];
    if (!sp) return;
    // Session 6b item 8: ALL spell types (damage / debuff / heal / buff)
    // need the player to pick a target tile. Heal / Bless used to
    // auto-target the whole team; now they only affect friendlies inside
    // SPELLS[type].radius around the chosen tile.
    this.targeting = true;
    this.schedStep = 'target';
  }

  confirmTarget(bx: number, by: number): void {
    if (!this.targeting) return;
    // Story Mode — only accept the prescribed target during forced steps.
    if (this.storyController) {
      if (this.storyController.shouldPauseGame()) return;
      if (!this.storyController.isSpellTargetAllowed(this.spellSlot, this.selSpellType, { x: bx, y: by })) return;
    }
    // Points & Tiers: pRes[el] is raw accumulated points; convert to the
    // tier (powerLevel) for the SpellCommand. Guard against the
    // degenerate "<10 points" case by falling back to Lv1 (validator
    // will reject it server-side, but that's safer than slot=0).
    const elPoints = this.pRes[this.selSpellType] || 0;
    const lv = Math.max(1, pointsToLevel(elPoints));
    this.pSpells[this.spellSlot] = {
      slotIndex: this.spellSlot, spellType: this.selSpellType,
      powerLevel: lv, targetPos: { x: bx, y: by },
    };
    this.usedSpellEls.push(this.selSpellType);
    this.targeting = false;
    // BUGFIX: clear the targeting cursor too. Without this, next slot's
    // `selectSpell` re-enables `targeting`, and Renderer's targeting
    // overlay (`g.targeting && g.targetPos`) draws a stale radius
    // preview at the PREVIOUS slot's target cell until the player moves
    // the cursor.
    this.targetPos = null;
    if (this.storyController) {
      this.storyController.onSpellApplied(this.spellSlot, this.selSpellType, { x: bx, y: by });
    }
    this._nextSlot();
  }

  skipSlot(): void {
    // Story Mode — disallow Pass during a specific-element forced step.
    // Wildcard `expectAnySpell` (el === -1) allows Pass as a valid
    // satisfaction (player has no usable spells left = legitimate skip).
    if (this.storyController?.expectedSpell
        && this.storyController.expectedSpell.slot === this.spellSlot
        && this.storyController.expectedSpell.el !== -1) return;
    this.pSpells[this.spellSlot] = { slotIndex: this.spellSlot, spellType: 0, powerLevel: 0 };
    // Notify controller so expectAnySpell can advance on Pass too.
    if (this.storyController) {
      this.storyController.onSpellApplied(this.spellSlot, 0, { x: 0, y: 0 });
    }
    this._nextSlot();
  }

  private _nextSlot(): void {
    this.spellSlot++;
    if (this.spellSlot >= 3) {
      // Session 6c item 6: don't auto-submit. Wait for confirmSchedule()
      // OR for the SCHED timer to expire (handled in update()).
      this.schedStep = 'confirm';
    } else {
      this.schedStep = 'slot';
      this._refreshAvailSpells();
    }
  }

  /**
   * Session 6c item 6 — undo the most recent scheduling step.
   *  - From 'target' (mid-spell-pick) → cancel back to 'slot' for same slot
   *  - From 'slot' (cursor on slot N>0)      → roll cursor back to N-1, clear it
   *  - From 'confirm' (all 3 filled, awaiting confirm) → roll back to slot 2
   *  - From 'slot' with cursor at slot 0     → no-op
   */
  goBackSlot(): void {
    if (this.state !== GS.SCHED) return;
    // Story Mode — allow mid-pick cancel (back from 'target' to 'slot'
    // on the SAME slot, no commit involved), but block any roll-back
    // that would undo a slot the script has already advanced past.
    const storyBlocks = this.gameMode === 'story' && this.storyController;
    if (this.schedStep === 'target') {
      this.targeting = false;
      this.targetPos = null;
      this.selSpellType = 0;
      this.schedStep = 'slot';
      this._refreshAvailSpells();
      return;
    }
    if (storyBlocks) return; // no undo of committed slots in tutorial
    // 'slot' or 'confirm'
    if (this.schedStep === 'confirm') {
      this.spellSlot = 2;
    } else {
      // 'slot' — currently editing slot N. Roll cursor back to N-1 to redo
      // that slot (so user can change a previous decision mid-flow).
      if (this.spellSlot <= 0) return;
      this.spellSlot--;
    }
    const prev = this.pSpells[this.spellSlot];
    if (prev && prev.spellType > 0) {
      this.usedSpellEls = this.usedSpellEls.filter(el => el !== prev.spellType);
    }
    this.pSpells[this.spellSlot] = null;
    this.schedStep = 'slot';
    this._refreshAvailSpells();
  }

  /** Session 6c item 6 — finalize after the confirm button is pressed. */
  confirmSchedule(): void {
    if (this.state !== GS.SCHED) return;
    if (this.schedStep !== 'confirm') return;
    // Story Mode — defensive: block if there's still an active expectSpell
    // / expectAnySpell constraint pending. In practice schedStep === 'confirm'
    // only reaches here after all 3 slots are committed, but the check
    // guards against any flow surprises.
    if (this.gameMode === 'story' && this.storyController) {
      if (!this.storyController.isConfirmSchedAllowed()) return;
    }
    this._finalizeSched();
  }

  /**
   * Session 6c follow-up — manually switch the bottom interaction tab.
   * Player can use this during any battle phase to peek at the other
   * view (e.g. check the planned schedule while EXEC plays out).
   */
  setBottomTab(tab: 'match3' | 'sched'): void {
    if (this.state !== GS.MATCH3 && this.state !== GS.SCHED && this.state !== GS.EXEC) return;
    this.bottomTab = tab;
  }

  private _finalizeSched(): void {
    // Story Mode — let the controller clear arrow / hint overlays before
    // EXEC kicks off so the battlefield isn't cluttered.
    if (this.storyController) this.storyController.onSchedConfirmed();
    for (let i = 0; i < 3; i++) {
      if (!this.pSpells[i]) this.pSpells[i] = { slotIndex: i, spellType: 0, powerLevel: 0 };
      if (!this.aSpells[i]) this.aSpells[i] = { slotIndex: i, spellType: 0, powerLevel: 0 };
    }
    if (this.adapter) {
      // Phase 3d Step 4 fix: in PvP, our pSpells targetPos are in VIEW
      // coords (set by confirmTarget from the user's click). Flip them
      // back to world before sending so server-side spell resolution
      // sees coordinates relative to the authoritative world grid.
      // In single mode, viewFlipped is false so this is identity.
      const myScheduleWorld = this.pSpells.map((c) => this._cmdViewToWorld(c));
      void this.adapter.submitSchedule(this.myTeam, myScheduleWorld).catch((err) => {
        console.warn('[game] submitSchedule(' + this.myTeam + ') failed:', (err as Error).message);
      });
      if (this._isLocalMode()) {
        // aSpells in single/story modes are AI-generated using unit.pos which
        // is also in view coords (== world for myTeam=0), so no flip needed.
        const oppTeam = (this.myTeam === 0 ? 1 : 0) as 0 | 1;
        void this.adapter.submitSchedule(oppTeam, this.aSpells as SpellCommand[]).catch((err) => {
          console.warn('[game] submitSchedule(' + oppTeam + ') failed:', (err as Error).message);
        });
      }
    }
    // _startExec is async; we don't await because update(dt) is the game loop
    // driver. While exec is pending the stepActive flag keeps the frame loop
    // idle — see _startExec / _updateExec below.
    void this._startExec();
  }

  private async _startExec(): Promise<void> {
    this.state = GS.EXEC;
    // Story Mode — resume any parked `waitForPhase: 'exec'` step.
    if (this.gameMode === 'story' && this.storyController) {
      this.storyController.notifyPhaseEntered('exec');
    }
    this.transitionTimer = 0.6;
    this.transitionLabel = 'Battle!';
    this.targeting = false;
    // Session 6c follow-up: default to MATCH3 tab during EXEC so the player
    // sees the board (still dimmed read-only) during the battle animation;
    // they can flip to the 'sched' tab to review the locked-in schedule.
    this.bottomTab = 'match3';
    this.phaseBanner = null;
    anim.clear();

    // Reset event playback machinery; stepActive stays false until the
    // server returns the event log, so _updateExec won't start playing
    // a stale or empty list.
    this.events = [];
    this.stepIdx = 0;
    this.stepTimer = 0;
    this.stepApplied = false;
    this.stepActive = false;

    if (!this.adapter) return;

    // Phase 3d Step 4: PvP mode does NOT call executeExec. Server gates
    // exec on both schedules and pushes execComplete to both clients.
    // The push handler (installed in startCreateRoom/startJoinRoom)
    // sets this.events + this.stepActive = true. Single + story take
    // the local path below.
    if (!this._isLocalMode()) {
      this.execPending = true;
      this.waitingForOpponent = true;
      return;
    }

    this.execPending = true;
    this.adapterError = null;
    try {
      const res = await this.adapter.executeExec({
        playerSpells: this.pSpells as [SpellCommand, SpellCommand, SpellCommand],
        aiSpells: this.aSpells as [SpellCommand, SpellCommand, SpellCommand],
      });
      this.events = res.events;
    } catch (err) {
      this.adapterError = `executeExec failed: ${(err as Error).message}`;
      console.error('[game]', this.adapterError);
      this.events = [];
    } finally {
      this.execPending = false;
    }
    this.stepActive = true;
  }

  private _updateExec(dt: number): void {
    if (!this.stepActive) return;
    if (this.stepIdx >= this.events.length) {
      this._finishExec(); return;
    }
    const event = this.events[this.stepIdx];

    // Story Mode — pause EXEC playback while a tutorial dialogue is on
    // screen. Also: BEFORE applying a new event, ask the controller if a
    // pre-event tutorial pause should fire (show dialogue, stall here
    // until dismissed). This is how tick 1/2 sub-step explanations are
    // injected without touching the server event log.
    if (this.gameMode === 'story' && this.storyController) {
      if (this.storyController.shouldPauseExec()) return;
      if (!this.stepApplied) {
        const lines = this.storyController.consumeExecPauseFor(event as any);
        if (lines) {
          this.storyController.showRuntimeDialogue(lines);
          return; // wait for player to dismiss
        }
      }
    }

    this.stepTimer += dt;

    if (!this.stepApplied) {
      this.stepApplied = true;
      this._applyEvent(event);
    }

    if (this.stepTimer >= event.dur && !anim.busy) {
      this.stepIdx++;
      this.stepTimer = 0;
      this.stepApplied = false;
    }
  }

  private _applyEvent(event: BattleEvent): void {
    // Story Mode — give the controller a peek at the event so it can
    // fire conditional tips (archer-in-grass, lightning-on-puddle, etc).
    // Tips are show-once + only after the scripted portion ends.
    if (this.storyController) {
      const terrainAt = (x: number, y: number): string => {
        if (!this.terrain) return 'none';
        const row = this.terrain[y];
        if (!row) return 'none';
        return row[x] || 'none';
      };
      this.storyController.observeBattleEvent(
        event as any,
        {
          units: this.units.map((u) => ({ id: u.id, type: u.type, pos: u.pos })),
          terrainAt,
        },
      );
    }
    switch (event.type) {
      case 'tickLabel':
        // Session 10 follow-up — tick boundary modifier decrement.
        // Server decrements at end-of-tick (after Step F); the wire
        // doesn't carry an explicit "tick ended" event, so the client
        // uses each tickLabel (tick > 0) as the "previous tick just
        // ended" signal. Final tick's end-of-tick fires in `_finishExec`.
        if (event.tick && event.tick > 0) {
          this._decrementUnitModifiers();
        }
        this.fx.push({
          type: 'floatText', x: W / 2, y: ZBY + OYB + CB * 4,
          text: `⚔ Tick ${event.tick! + 1}`,
          color: '#ffdd44', alpha: 1.5, timer: 1.2, size: 20,
        });
        break;

      case 'spell': {
        // Phase banner — Session 6a split spell into friendly (heal/buff
        // at tick start) vs hostile (damage/debuff at tick end before
        // contest). Sticky banner (Session 6c follow-up): set
        // `phaseBanner` and let it sit until the next sub-step replaces it.
        const phase = (event as { phase?: 'friendly' | 'hostile' }).phase;
        const isFriendly = phase === 'friendly';
        this.phaseBanner = {
          text: isFriendly ? '✦ 輔助魔法 ✦' : '✦ 攻擊魔法 ✦',
          color: isFriendly ? '#44ddaa' : '#ffaa44',
        };
        const spellList = event.spells || [];
        for (const se of spellList) {
          // Apply impacts to units
          for (const impact of se.impacts) {
            const u = this.units.find(x => x.id === impact.unitId);
            if (!u) continue;
            u.totalSoldiers = impact.totalSoldiers;
            if (impact.died) { u.dead = true; u.totalSoldiers = 0; }

            // Session 10 follow-up — mirror server's modifier push on the
            // client so Renderer can draw buff/debuff badges on the unit.
            // Server's authoritative modifier state lives in StateManager;
            // Game.units is a separate copy (cloneState splits them at
            // initBattle), so we have to apply the same push locally to
            // keep the visual list in sync.
            //
            // Look up the matching EffectKind in the spell's effects[] to
            // get its durationTicks (impact wire format only carries the
            // applied VALUE, not the duration).
            //
            // Session 11 — rooted joins the mirror set. `impact.rooted`
            // carries the duration directly (= the modifier's initial
            // ticksRemaining), so we don't need to peek at the spell
            // def for it; the wire ships the number we need.
            //
            // Session 11 follow-up — defDown is mirrored similarly. The
            // impact field carries the per-modifier value; duration is
            // derived from the SpellDef's `defDownDurationTicks` (= 2
            // for hostile Grow). Mutually exclusive with rooted on the
            // same impact (the SpellDef branches per-target).
            const impactKind: 'buffAtk' | 'shieldPhys' | 'shieldMag' | 'rooted' | 'defDown' | null =
                impact.buff !== undefined ? 'buffAtk'
              : impact.defPhys !== undefined ? 'shieldPhys'
              : impact.defMag !== undefined ? 'shieldMag'
              : impact.rooted !== undefined && impact.rooted > 0 ? 'rooted'
              : impact.defDown !== undefined && impact.defDown > 0 ? 'defDown'
              : null;
            if (impactKind) {
              let duration: number;
              let value: number;
              if (impactKind === 'rooted') {
                duration = impact.rooted ?? 1;
                value = 1; // rooted is presence-only; value is ignored by _recomputeScalars
              } else if (impactKind === 'defDown') {
                // growGrass effect bundles defDown — pull the durationTicks
                // from there. Wire carries the per-mod value already.
                const eff = se.sp.effects.find((e) => e.kind === 'growGrass') as
                  | { defDownDurationTicks?: number } | undefined;
                duration = (eff?.defDownDurationTicks ?? 2);
                value = impact.defDown ?? 0;
              } else {
                const eff = se.sp.effects.find((e) => e.kind === impactKind) as
                  | { durationTicks?: number } | undefined;
                duration = (eff?.durationTicks ?? 1);
                value = impactKind === 'buffAtk'
                  ? (impact.buff ?? 0)
                  : impactKind === 'shieldPhys'
                    ? (impact.defPhys ?? 0)
                    : (impact.defMag ?? 0);
              }
              _pushModifier(u, impactKind, value, duration, se.cmd.spellType);
            }
          }
          // Session 9 — whirlwind embeds MoveRecord[] (one per unit
          // actually displaced). Mirror the server's state mutation on
          // the local model AND animate the slide so the player sees
          // the displacement.
          //
          // Session 10 follow-up — KEEP the unit's drawn path. Path is
          // the player's intended route; the unit just gets nudged off
          // it for a tick or two and walks back via _stepToward (see
          // movement.ts priority 2). Auto-advance `pathIdx` to the
          // closest remaining waypoint so units blown PAST waypoints
          // don't backtrack — must mirror server `spell.ts._applyWhirlwind`.
          if (Array.isArray(se.moves) && se.moves.length > 0) {
            for (const m of se.moves) {
              const u = this.units.find((x) => x.id === m.unitId);
              if (!u) continue;
              const dest = { x: m.to.x, y: m.to.y };
              u.pos = dest;
              // Mirror the server's pathIdx auto-advance logic so the
              // client's render-time path preview matches what movement
              // events will deliver next tick.
              let bestIdx = u.pathIdx;
              let bestDist = bestIdx < u.path.length ? md(dest, u.path[bestIdx]) : Infinity;
              for (let i = u.pathIdx + 1; i < u.path.length; i++) {
                const d = md(dest, u.path[i]);
                if (d < bestDist) { bestDist = d; bestIdx = i; }
              }
              while (
                bestIdx < u.path.length &&
                u.path[bestIdx].x === dest.x &&
                u.path[bestIdx].y === dest.y
              ) {
                bestIdx++;
              }
              u.pathIdx = bestIdx;
              // Slide tween — overlap with the whirlwind ring fx.
              anim.add(u as any, { vx: m.to.x, vy: m.to.y }, 0.6, 'easeOut');
              // Light trail line for visibility.
              const fx = OXB + m.from.x * CB + CB / 2;
              const fy = OYB + m.from.y * CB + CB / 2;
              const tx = OXB + m.to.x * CB + CB / 2;
              const ty = OYB + m.to.y * CB + CB / 2;
              const tcol = u.team === this.myTeam ? '#4488ff' : '#ff4444';
              this.fx.push({
                type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
                color: tcol, alpha: 0.7, timer: 0.7,
              });
            }
          }
          this._renderSpellFx(se as any);
          // c 段 — fire ignite particles SYNCHRONOUSLY with the fireball
          // animation. The state-mutation `terrainIgnite` event still
          // arrives in the log right after this spell event but doesn't
          // produce visuals (it's silent now). Predicting which cells
          // ignite by scanning the spell radius for current 'grass' on
          // the local terrain is safe because the ignite event hasn't
          // been applied yet — terrain still holds 'grass' here.
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Fire'
          ) {
            this._spawnIgniteFx(se.tgt, se.sp.radius);
          }
          // Session 11 (Leader CCC) — Grow / Rain mirror the Fire ignite
          // pattern: paint client-side terrain synchronously so the cell
          // visually flips green/blue AT THE SPELL HIT FRAME, not 0.05s
          // later when the trailing terrainGrow/terrainRain event arrives.
          // The trailing event is idempotent — it only flips cells still
          // showing 'none', so double-painting is a no-op.
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Grow'
          ) {
            this._spawnGrowFx(se.tgt, se.sp.radius);
          }
          // Session 12 — Rockfall mirrors Grow/Rain's predictive paint.
          // For each cell in radius (= just the center for radius=0):
          //   - if no unit was on it AT CAST START → predict boulder paint
          //   - if a unit was on it → spawn shatter dust (no boulder)
          // We derive "had a unit at cast start" from `se.impacts` (cells
          // where rockfall actually damaged a unit, including units it
          // killed this cast — which would already show u.dead=true in
          // this.units by the time we render). The trailing
          // terrainBoulderFall event re-asserts the paint authoritatively.
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Rockfall'
          ) {
            this._spawnRockfallFx(se);
          }
          // Session 12 — Fire / Lightning shatter boulders in radius. Same
          // predictive pattern: client mutates terrain at the hit frame,
          // trailing terrainBoulderBreak event confirms authoritatively.
          if (
            phase === 'hostile' &&
            this.terrain &&
            (ELEM[se.cmd.spellType] === 'Fire' || ELEM[se.cmd.spellType] === 'Lightning')
          ) {
            this._spawnRockfallBreakFx(se.tgt, se.sp.radius);
          }
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Rain'
          ) {
            this._spawnRainFx(se.tgt, se.sp.radius);
            // Session 11 follow-up — 淹水特效：when Rain lands on a target
            // that's standing on a pre-existing puddle (= the "drown bonus"
            // branch), spawn an extra flood burst on that cell. Per-impact
            // so radius-1 hits can mix in/out puddle targets, each getting
            // its own visual confirmation.
            for (const impact of se.impacts) {
              const tgt = this.units.find((x) => x.id === impact.unitId);
              if (!tgt) continue;
              const row = this.terrain[tgt.pos.y];
              if (!row) continue;
              if (row[tgt.pos.x] !== 'puddle') continue;
              this._spawnFloodFx(tgt.pos);
            }
          }
          // fix#5 — Lightning hitting a unit standing in a puddle gets
          // an extra "electrified water" burst on top of the standard
          // lightning fx. Per-target since radius-1 hits can mix in /
          // out puddle (matches the spell.ts +30% rule).
          if (
            phase === 'hostile' &&
            this.terrain &&
            ELEM[se.cmd.spellType] === 'Lightning'
          ) {
            for (const impact of se.impacts) {
              const u = this.units.find((x) => x.id === impact.unitId);
              if (!u) continue;
              const row = this.terrain[u.pos.y];
              if (!row) continue;
              if (row[u.pos.x] !== 'puddle') continue;
              this._spawnPuddleZapFx(u.pos);
            }
          }
        }
        break;
      }

      case 'move': {
        // Phase banner — sticky (Session 6c follow-up)
        this.phaseBanner = { text: '➤ 士兵移動 ➤', color: '#44ccff' };

        // Apply final positions eagerly. event.moves is in chronological
        // order (sub-phase 0 then sub-phase 1), so advancing pathIdx
        // step-by-step here keeps the rendered path preview aligned with
        // the unit's actual progress — Renderer._battlefield draws from
        // pathIdx onward, so unwalked waypoints are the only ones shown.
        for (const m of event.moves) {
          const u = this.units.find(x => x.id === m.unitId);
          if (!u) continue;
          u.pos = { ...m.to };
          if (u.pathIdx < u.path.length) {
            const wp = u.path[u.pathIdx];
            if (wp && wp.x === m.to.x && wp.y === m.to.y) {
              u.pathIdx += 1;
            }
          }
        }
        for (const c of event.contested) {
          const uA = this.units.find(x => x.id === c.unitAId);
          const uB = this.units.find(x => x.id === c.unitBId);
          if (uA) {
            uA.pos = { ...c.pos };
            if (uA.pathIdx < uA.path.length) {
              const wpA = uA.path[uA.pathIdx];
              if (wpA && wpA.x === c.pos.x && wpA.y === c.pos.y) uA.pathIdx += 1;
            }
          }
          if (uB) {
            uB.pos = { ...c.pos };
            if (uB.pathIdx < uB.path.length) {
              const wpB = uB.path[uB.pathIdx];
              if (wpB && wpB.x === c.pos.x && wpB.y === c.pos.y) uB.pathIdx += 1;
            }
          }
        }

        // Build friendly stacking offset map (after apply, pos reflects final positions)
        const stackMap = new Map<string, BattleUnit[]>();
        for (const u of this.units) {
          if (u.dead) continue;
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          if (!stackMap.has(key)) stackMap.set(key, []);
          stackMap.get(key)!.push(u);
        }
        /** Get visual X offset for a unit due to friendly stacking */
        const getStackOff = (u: BattleUnit): number => {
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          const group = stackMap.get(key);
          if (!group || group.length <= 1) return 0;
          const idx = group.indexOf(u);
          return (idx - (group.length - 1) / 2) * 0.35;
        };

        // Build set of contested unit IDs for special animation
        const contestedIds = new Set<string>();
        if (event.contested) {
          for (const c of event.contested) {
            contestedIds.add(c.unitAId);
            contestedIds.add(c.unitBId);
          }
        }

        // Group move entries by unit to chain multi-step animations
        const movesByUnit = new Map<string, { unit: BattleUnit; entries: typeof event.moves }>();
        for (const m of event.moves!) {
          const u = this.units.find(x => x.id === m.unitId);
          if (!u) continue;
          if (!movesByUnit.has(m.unitId)) movesByUnit.set(m.unitId, { unit: u, entries: [] });
          movesByUnit.get(m.unitId)!.entries!.push(m);
        }

        // Animate non-contested units (with friendly stacking offset)
        for (const [uid, { unit: u, entries }] of movesByUnit) {
          if (contestedIds.has(uid)) continue; // handled below
          const tcol = u.team === this.myTeam ? '#4488ff' : '#ff4444';
          const sOff = getStackOff(u);
          // Draw trail lines for all steps
          for (const m of entries!) {
            const fx = OXB + m.from.x * CB + CB / 2;
            const fy = OYB + m.from.y * CB + CB / 2;
            const tx = OXB + m.to.x * CB + CB / 2;
            const ty = OYB + m.to.y * CB + CB / 2;
            this.fx.push({
              type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
              color: tcol, alpha: 0.8, timer: 0.8,
            });
          }
          // Final arrow
          const last = entries![entries!.length - 1];
          const lfx = OXB + entries![0].from.x * CB + CB / 2;
          const lfy = OYB + entries![0].from.y * CB + CB / 2;
          const ltx = OXB + last.to.x * CB + CB / 2;
          const lty = OYB + last.to.y * CB + CB / 2;
          this.fx.push({
            type: 'moveArrow', x1: lfx, y1: lfy, x2: ltx, y2: lty,
            color: tcol, alpha: 1.0, timer: 0.8,
          });
          // Chain animations with stacking offset on final destination.
          // c 段 / fix#4 — fire water splash at the START of the move
          // that lands on a puddle, so the splash overlaps with the
          // unit's walk-in rather than appearing only after it stops.
          if (entries!.length === 1) {
            const dest = entries![0].to;
            this._splashIfPuddle(dest);
            anim.add(u as any, { vx: dest.x + sOff, vy: dest.y }, 0.7, 'easeOut');
          } else {
            let si = 0;
            const animateNext = () => {
              if (si >= entries!.length) return;
              const m = entries![si];
              si++;
              // Only apply stack offset on the very last step
              const off = si >= entries!.length ? sOff : 0;
              // Splash fires as THIS step begins, so the droplets are
              // visible during the 0.5s walk-in.
              this._splashIfPuddle(m.to);
              anim.add(u as any, { vx: m.to.x + off, vy: m.to.y }, 0.5, 'easeOut', animateNext);
            };
            animateNext();
          }
        }

        // Also animate units that didn't move but may need stacking offset update
        for (const u of this.units) {
          if (u.dead) continue;
          if (movesByUnit.has(u.id)) continue; // already animated
          if (contestedIds.has(u.id)) continue;
          const sOff = getStackOff(u);
          if (Math.abs(u.vx - (u.pos.x + sOff)) > 0.01 || Math.abs(u.vy - u.pos.y) > 0.01) {
            anim.add(u as any, { vx: u.pos.x + sOff, vy: u.pos.y }, 0.4, 'easeOut');
          }
        }

        // Animate contested units — each occupies half the tile
        if (event.contested) {
          for (const c of event.contested) {
            const uA = this.units.find(x => x.id === c.unitAId);
            const uB = this.units.find(x => x.id === c.unitBId);
            if (!uA || !uB) continue;
            // Offset: team 0 left (-0.25), team 1 right (+0.25)
            const offA = uA.team === this.myTeam ? -0.25 : 0.25;
            const offB = uB.team === this.myTeam ? -0.25 : 0.25;
            // Both contesting units land on the same tile — one splash
            // covers both visually, fired as the walk-in starts (fix#4).
            this._splashIfPuddle(c.pos);
            anim.add(uA as any, { vx: c.pos.x + offA, vy: c.pos.y }, 0.7, 'easeOut');
            anim.add(uB as any, { vx: c.pos.x + offB, vy: c.pos.y }, 0.7, 'easeOut');
            // Trail FX for both
            for (const e of [
              { unit: uA, from: c.unitAFrom },
              { unit: uB, from: c.unitBFrom },
            ]) {
              const tcol = e.unit.team === this.myTeam ? '#4488ff' : '#ff4444';
              const fx = OXB + e.from.x * CB + CB / 2;
              const fy = OYB + e.from.y * CB + CB / 2;
              const tx = OXB + c.pos.x * CB + CB / 2;
              const ty = OYB + c.pos.y * CB + CB / 2;
              this.fx.push({
                type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
                color: tcol, alpha: 0.8, timer: 0.8,
              });
            }
            // Contested indicator — crossed swords
            const cx = OXB + c.pos.x * CB + CB / 2;
            const cy = OYB + c.pos.y * CB + CB / 2;
            this.fx.push({
              type: 'floatText', x: cx, y: cy - 16,
              text: '⚔', color: '#ffaa00', alpha: 2.0, timer: 1.5, size: 16,
            });
          }
        }
        break;
      }

      case 'combat': {
        // Phase banner — sticky (Session 6c follow-up)
        this.phaseBanner = { text: '⚔ 士兵攻擊 ⚔', color: '#ff6644' };

        const typeLabels: Record<string, string> = {
          Mage: '法師', Archer: '弓兵', Cavalry: '騎兵', Infantry: '步兵',
        };
        const atkGroups = event.groups;

        // Chain group animations sequentially
        let gi = 0;
        const animateGroup = () => {
          if (gi >= atkGroups.length) return;
          const grp = atkGroups[gi];
          gi++;

          // Apply this type group's damage NOW (synced with animation)
          for (const ud of grp.unitDamages) {
            const u = this.units.find(x => x.id === ud.unitId);
            if (u) {
              u.totalSoldiers = ud.totalSoldiers;
              if (ud.died) { u.dead = true; u.totalSoldiers = 0; }
            }
          }
          for (const bd of grp.baseDamages) {
            const b = this.bases.find(x => x.team === bd.team);
            if (b) {
              b.hp = bd.hp;
              if (bd.destroyed) b.destroyed = true;
            }
          }

          // Type label — Session 6c follow-up: positioned mid-battlefield
          // so it doesn't crash into the sticky phase banner above OYB.
          // Skip when the group is empty (no soldiers of this type
          // attacked this tick).
          if (grp.attacks && grp.attacks.length > 0) {
            this.fx.push({
              type: 'floatText', x: W / 2, y: OYB + CB * (BATTLE_ROWS / 2),
              text: `${typeLabels[grp.groupType]} 攻擊！`,
              color: '#ffcc44', alpha: 1.5, timer: 0.8, size: 13,
            });
          }

          for (const a of grp.attacks!) {
            const attacker = this.units.find(x => x.id === a.attackerId);
            if (!attacker) continue;

            const ax = OXB + a.attackerPos.x * CB + CB / 2;
            const ay = OYB + a.attackerPos.y * CB + CB / 2;

            let tx: number, ty: number;
            if (a.targetUnitPos) {
              tx = OXB + a.targetUnitPos.x * CB + CB / 2;
              ty = OYB + a.targetUnitPos.y * CB + CB / 2;
            } else if (a.targetBasePos) {
              tx = OXB + a.targetBasePos.x * CB + CB / 2;
              ty = OYB + a.targetBasePos.y * CB + CB / 2;
            } else continue;

            const atkCol = attacker.team === this.myTeam ? '#88bbff' : '#ff8888';

            // Attacker lunge animation — dash toward target then snap back
            const lungeX = a.attackerPos.x + (tx - ax) / CB * 0.35;
            const lungeY = a.attackerPos.y + (ty - ay) / CB * 0.35;
            const origX = a.attackerPos.x;
            const origY = a.attackerPos.y;
            anim.add(attacker as any, { vx: lungeX, vy: lungeY }, 0.2, 'easeOut', () => {
              anim.add(attacker as any, { vx: origX, vy: origY }, 0.25, 'bounce');
            });

            // Attacker flash
            attacker.flash = 0.3;
            attacker.flashColor = atkCol;

            // Slash line
            this.fx.push({
              type: 'slash', x1: ax, y1: ay, x2: tx, y2: ty,
              color: atkCol, alpha: 1.0, timer: 0.8,
            });
            // Damage number
            this.fx.push({
              type: 'floatText', x: tx + (Math.random() - 0.5) * 10, y: ty - 12,
              text: '-' + a.dmg, color: '#ff6644', alpha: 1.8, timer: 1.2, size: 13,
            });
            // Hit particles
            for (let p = 0; p < 4; p++) {
              this.fx.push({
                type: 'particle', x: tx, y: ty,
                dx: (Math.random() - 0.5) * 120,
                dy: -30 - Math.random() * 60,
                color: atkCol, alpha: 1.2, timer: 0.8, size: 3,
              });
            }

            if (a.targetUnitId) {
              const target = this.units.find(x => x.id === a.targetUnitId);
              if (target) {
                target.flash = 0.35;
                target.flashColor = '#ff4444';
                if (a.targetDied) {
                  this.fx.push({
                    type: 'deathX', x: tx, y: ty,
                    color: '#ff2222', alpha: 1.5, timer: 1.8, size: CB * 0.4, scale: 1,
                  });
                  for (let p = 0; p < 8; p++) {
                    this.fx.push({
                      type: 'particle', x: tx, y: ty,
                      dx: (Math.random() - 0.5) * 180,
                      dy: -50 - Math.random() * 80,
                      color: target.team === this.myTeam ? '#4488ff' : '#ff4455',
                      alpha: 1.5, timer: 0.8, size: 4,
                    });
                  }
                }
              }
            }

            if (a.baseDied) {
              const targetBase = this.bases.find(x => x.team === a.targetBaseTeam);
              for (let p = 0; p < 12; p++) {
                const angle = (p / 12) * Math.PI * 2;
                this.fx.push({
                  type: 'particle', x: tx, y: ty,
                  dx: Math.cos(angle) * 140,
                  dy: Math.sin(angle) * 140,
                  color: targetBase && targetBase.team === this.myTeam ? '#4488ff' : '#ff4455',
                  alpha: 2.0, timer: 1.2, size: 5,
                });
              }
              this.fx.push({
                type: 'ring', x: tx, y: ty, radius: 5,
                color: '#ff8800', alpha: 2.0, timer: 1.5,
              });
            }
          }

          // Sentinel tween to chain next group after this group's lunge/bounce completes
          const sentinel = { _t: 0 };
          anim.add(sentinel as any, { _t: 1 }, 0.55, 'linear', animateGroup);
        };
        animateGroup();
        break;
      }

      case 'contestResolve': {
        // Phase banner — sticky (Session 6c follow-up)
        this.phaseBanner = { text: '⚔ 爭奪結算 ⚔', color: '#ffaa44' };
        
        // Apply final positions eagerly
        if (event.retreats) {
          for (const r of event.retreats) {
            const u = this.units.find(x => x.id === r.unitId);
            if (u) u.pos = { ...r.to };
          }
        }
        if (event.stays) {
          for (const s of event.stays) {
            const u = this.units.find(x => x.id === s.unitId);
            if (u) u.pos = { ...s.pos };
          }
        }

        // Build stacking offset map for retreat destinations
        const crStackMap = new Map<string, BattleUnit[]>();
        for (const u of this.units) {
          if (u.dead) continue;
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          if (!crStackMap.has(key)) crStackMap.set(key, []);
          crStackMap.get(key)!.push(u);
        }
        const crStackOff = (u: BattleUnit): number => {
          const key = `${u.team},${u.pos.x},${u.pos.y}`;
          const group = crStackMap.get(key);
          if (!group || group.length <= 1) return 0;
          const idx = group.indexOf(u);
          return (idx - (group.length - 1) / 2) * 0.35;
        };

        // Animate retreating units back to their previous position
        if (event.retreats) {
          for (const r of event.retreats) {
            const u = this.units.find(x => x.id === r.unitId);
            if (!u) continue;
            const rOff = crStackOff(u);
            anim.add(u as any, { vx: r.to.x + rOff, vy: r.to.y }, 0.6, 'easeOut');
            // Retreat trail
            const fx = OXB + r.from.x * CB + CB / 2;
            const fy = OYB + r.from.y * CB + CB / 2;
            const tx = OXB + r.to.x * CB + CB / 2;
            const ty = OYB + r.to.y * CB + CB / 2;
            const tcol = u.team === this.myTeam ? '#4488ff' : '#ff4444';
            this.fx.push({
              type: 'moveTrail', x1: fx, y1: fy, x2: tx, y2: ty,
              color: tcol, alpha: 0.6, timer: 0.6,
            });
            // "Retreat" label
            this.fx.push({
              type: 'floatText', x: tx, y: ty - 12,
              text: '撤退', color: '#ff8844', alpha: 1.5, timer: 1.0, size: 11,
            });
          }
        }

        // Animate staying units to tile center (from half-tile offset)
        if (event.stays) {
          for (const s of event.stays) {
            const u = this.units.find(x => x.id === s.unitId);
            if (u) anim.add(u as any, { vx: s.pos.x, vy: s.pos.y }, 0.4, 'easeOut');
          }
        }
        break;
      }

      case 'terrainIgnite': {
        // a-2 / c 段 / fix#3v2 — IDEMPOTENT SAFETY NET. The grass→
        // burningGrass mutation now happens synchronously inside the
        // preceding `spell` event (so the cell flips at the fireball
        // explosion frame, not after). This event arrives next with the
        // authoritative server-side cell list; for any cell still
        // showing as 'grass' we flip it (covers edge cases where the
        // client's prediction missed something, e.g. server-only
        // terrain drift). Cells already burning are silently ignored.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            if (row[c.x] === 'grass') row[c.x] = 'burningGrass';
          }
        }
        break;
      }

      case 'terrainGrow': {
        // Session 11 — authoritative confirmation of Grow's terrain
        // transitions. Each cell carries its `to` (destination terrain)
        // so we set unconditionally — the client-side predictor in
        // `_spawnGrowFx` may have already applied the same transition,
        // in which case this is a no-op write of the same value. If
        // the client predicted DIFFERENTLY (e.g. view-flip drift, or
        // future bugs in the transition table), the server's `to`
        // wins because it's authoritative.
        //
        // Session 11 (swamp/base-fix) — transitions are asymmetric so we
        // can NO LONGER replay the transition table client-side here
        // (e.g. server says 'puddle → swamp' but if the client already
        // turned the puddle into swamp, replaying would push swamp →
        // grass — wrong). The `to` field eliminates that ambiguity.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            row[c.x] = c.to;
          }
        }
        break;
      }

      case 'terrainRain': {
        // Session 11 — same idempotent-via-`to` pattern as terrainGrow.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            row[c.x] = c.to;
          }
        }
        break;
      }

      case 'terrainBoulderFall': {
        // Session 12 (Leader CCC, Rockfall) — authoritative confirmation
        // that one or more cells now hold a boulder. Client `_spawnRockfallFx`
        // already predicted the paint at the spell-hit frame; this event
        // re-applies unconditionally for any cells the prediction missed
        // (e.g. server-only edge cases). Boulder paint is unconditional —
        // we don't gate on the previous terrain.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            row[c.x] = 'boulder';
          }
        }
        break;
      }

      case 'terrainBoulderBreak': {
        // Session 12 — Fire / Lightning shattered these boulders back to
        // 'none'. Client may have already predicted this inside the
        // preceding `spell` event (_spawnRockfallBreakFx); this is the
        // safety-net authoritative apply.
        if (this.terrain) {
          for (const c of event.cells) {
            if (c.y < 0 || c.y >= this.terrain.length) continue;
            const row = this.terrain[c.y];
            if (!row || c.x < 0 || c.x >= row.length) continue;
            if (row[c.x] === 'boulder') row[c.x] = 'none';
          }
        }
        break;
      }

      case 'terrainDot': {
        // a-2 — Step F: each unit standing on burningGrass takes
        // ceil(maxSoldiers × 0.15) reduced by tickDefMag. The applies list
        // tells us exact post-damage state, so we just sync the model and
        // pop a floatText for each victim.
        this.phaseBanner = { text: '🔥 地形灼燒 🔥', color: '#ff7733' };
        for (const a of event.applies) {
          const u = this.units.find((x) => x.id === a.unitId);
          if (!u) continue;
          u.totalSoldiers = a.totalSoldiers;
          if (a.died) { u.dead = true; u.totalSoldiers = 0; }
          if (a.dmg > 0) {
            const cx = OXB + u.pos.x * CB + CB / 2;
            const cy = OYB + u.pos.y * CB + CB / 2;
            this.fx.push({
              type: 'floatText', x: cx, y: cy - 8,
              text: `-${a.dmg}`, color: '#ff7733',
              alpha: 1.6, timer: 1.0, size: 14,
            });
          }
        }
        break;
      }

      case 'gameEnd':
        break;
    }
  }

  /**
   * c 段 / fix#3v2 — spawn ignition fx AND mutate terrain for every
   * `'grass'` cell within Manhattan `radius` of `tgt` (view coords).
   * Called from the `spell` event handler so the cell visually flips
   * from grass to burningGrass AT THE MOMENT THE FIREBALL HITS, not
   * 2.2s later when the trailing `terrainIgnite` event fires.
   *
   * Mutation is safe to do client-side here because the client's local
   * `terrain` already mirrors the server's authoritative grid (same
   * generateTerrain seed). The trailing `terrainIgnite` event still
   * arrives but is idempotent — re-checks for `'grass'`, so flipping
   * a cell twice does nothing.
   */
  private _spawnIgniteFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    const FLAME_COLORS = ['#ff5522', '#ff8833', '#ffcc44', '#ffeb88'];
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (row[x] !== 'grass') continue;
        // Flip the cell immediately so the green→red-orange transition
        // overlaps the fireball explosion frame.
        row[x] = 'burningGrass';
        const cx = OXB + x * CB + CB / 2;
        const cy = OYB + y * CB + CB / 2;
        this.fx.push({
          type: 'ring', x: cx, y: cy, radius: 2, alpha: 2.0,
          color: '#ffeb88', timer: 0.30,
        });
        this.fx.push({
          type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.6,
          color: '#ff8833', timer: 0.55,
        });
        this.fx.push({
          type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.5,
          color: '#cc4422', timer: 0.70,
        });
        for (let p = 0; p < 10; p++) {
          const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
          const speed = 35 + Math.random() * 60;
          this.fx.push({
            type: 'particle',
            x: cx + (Math.random() - 0.5) * CB * 0.4,
            y: cy + (Math.random() - 0.5) * CB * 0.3,
            dx: Math.cos(ang) * speed,
            dy: Math.sin(ang) * speed,
            color: FLAME_COLORS[Math.floor(Math.random() * FLAME_COLORS.length)],
            alpha: 1.6,
            timer: 0.45 + Math.random() * 0.3,
            size: 1.5 + Math.random() * 2.5,
          });
        }
      }
    }
  }

  /**
   * Session 11 (Leader CCC) — spawn growth fx AND mutate terrain for every
   * cell within Manhattan `radius` of `tgt` (view coords) that the
   * server-side `growTransition` says will change. Client predicts the
   * paint synchronously with the spell hit frame; the trailing
   * `terrainGrow` event ships per-cell `to` to overwrite authoritatively.
   *
   * Session 11 (swamp/base-fix) — now branches by destination so:
   *   - 'none'  → 'grass'  : sprout particles
   *   - 'puddle'→ 'swamp'  : muddy-brown burst (water + vegetation)
   *   - 'swamp' → 'grass'  : sprout particles (vegetation wins)
   * Base tiles are skipped — matches server's _applyTerrainTransition.
   */
  private _spawnGrowFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    // Build base-tile lookup from this.bases (already in view coords).
    const baseTiles = new Set<string>();
    for (const b of this.bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (baseTiles.has(`${x},${y}`)) continue;
        const cur = row[x] as TerrainCell;
        const next = growTransition(cur);
        if (next === null || next === cur) continue;
        // Predict: write the destination now so terrain reads (cell pickers,
        // path overlays, etc) see the new state during the EXEC playback
        // gap. Trailing terrainGrow event will overwrite to the same value.
        row[x] = next;
        this._spawnGrowCellFx(x, y, cur, next);
      }
    }
  }

  /**
   * Per-cell visual for a Grow transition. Split out so terrainGrow event
   * could also spawn fx if needed in the future. Visuals branch on the
   * destination terrain: grass = sprout, swamp = mud burst.
   */
  private _spawnGrowCellFx(x: number, y: number, _from: TerrainCell, to: TerrainCell): void {
    const cx = OXB + x * CB + CB / 2;
    const cy = OYB + y * CB + CB / 2;
    if (to === 'grass') {
      const SPROUT_COLORS = ['#aaff66', '#88dd44', '#669933', '#ddff88'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 1.8, color: '#ddff88', timer: 0.30 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.4, color: '#88dd44', timer: 0.55 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.0, color: '#669933', timer: 0.80 });
      for (let p = 0; p < 8; p++) {
        const ang = -Math.PI / 2 + (Math.random() - 0.5) * 0.8;
        const speed = 30 + Math.random() * 45;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.5,
          y: cy + CB * 0.3,
          dx: Math.cos(ang) * speed,
          dy: Math.sin(ang) * speed,
          color: SPROUT_COLORS[Math.floor(Math.random() * SPROUT_COLORS.length)],
          alpha: 1.5, timer: 0.50 + Math.random() * 0.25, size: 1.5 + Math.random() * 2,
        });
      }
    } else if (to === 'swamp') {
      // Muddy burst — brown/olive palette to suggest "vegetation meets water"
      const MUD_COLORS = ['#6b5a2e', '#88a052', '#3d4a1f', '#aac070'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#88a052', timer: 0.35 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.1, color: '#6b5a2e', timer: 0.65 });
      // Splat particles in all directions, slow + heavy (suggesting mud)
      for (let p = 0; p < 10; p++) {
        const ang = Math.random() * Math.PI * 2;
        const speed = 20 + Math.random() * 35;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.3,
          y: cy + (Math.random() - 0.5) * CB * 0.2,
          dx: Math.cos(ang) * speed,
          dy: Math.sin(ang) * speed * 0.6,
          color: MUD_COLORS[Math.floor(Math.random() * MUD_COLORS.length)],
          alpha: 1.4, timer: 0.65 + Math.random() * 0.30, size: 2 + Math.random() * 2,
        });
      }
    }
  }

  /**
   * Session 11 (Leader CCC) — spawn rain fx AND mutate terrain for every
   * cell within Manhattan `radius` of `tgt` (view coords) that the
   * server-side `rainTransition` says will change. Mirrors `_spawnGrowFx`.
   *
   * Session 11 (swamp/base-fix) — branches by destination:
   *   - 'none'  → 'puddle' : raindrop ripples
   *   - 'grass' → 'swamp'  : muddy splatter (vegetation + water)
   *   - 'swamp' → 'puddle' : raindrop ripples (water wins)
   * Base tiles are skipped.
   */
  private _spawnRainFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    const baseTiles = new Set<string>();
    for (const b of this.bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (baseTiles.has(`${x},${y}`)) continue;
        const cur = row[x] as TerrainCell;
        const next = rainTransition(cur);
        if (next === null || next === cur) continue;
        row[x] = next;
        this._spawnRainCellFx(x, y, cur, next);
      }
    }
  }

  /**
   * Per-cell visual for a Rain transition. Branches on destination.
   * Session 11 follow-up — added `'none'` branch for the
   * `burningGrass → none` extinguish case (steam burst).
   */
  private _spawnRainCellFx(x: number, y: number, from: TerrainCell, to: TerrainCell): void {
    const cx = OXB + x * CB + CB / 2;
    const cy = OYB + y * CB + CB / 2;
    if (to === 'none' && from === 'burningGrass') {
      // 蒸汽噴發 — rain extinguishes the fire. Hissing white steam puff +
      // a couple of dying ember sparks fading downward as the flames die.
      const STEAM_COLORS = ['#ffffff', '#dddddd', '#cccccc', '#eef0f5'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.6, color: '#ffffff', timer: 0.35 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 1.0, color: '#cccccc', timer: 0.65 });
      // Steam cloud — particles drift upward + outward (lighter than air)
      for (let p = 0; p < 12; p++) {
        const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
        const speed = 30 + Math.random() * 45;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.4,
          y: cy + CB * 0.15,
          dx: Math.cos(ang) * speed * 0.6,
          dy: Math.sin(ang) * speed,
          color: STEAM_COLORS[Math.floor(Math.random() * STEAM_COLORS.length)],
          alpha: 1.4,
          timer: 0.70 + Math.random() * 0.40,
          size: 2.5 + Math.random() * 2.5,
        });
      }
      // 4 dying embers falling — fire being defeated
      for (let p = 0; p < 4; p++) {
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.3,
          y: cy + (Math.random() - 0.5) * CB * 0.2,
          dx: (Math.random() - 0.5) * 30,
          dy: 25 + Math.random() * 35,
          color: Math.random() > 0.5 ? '#cc4422' : '#995533',
          alpha: 1.2,
          timer: 0.45 + Math.random() * 0.25,
          size: 1.5 + Math.random() * 1.5,
        });
      }
      return;
    }
    if (to === 'puddle') {
      const DROP_COLORS = ['#88ccee', '#66aadd', '#aaddee', '#ffffff'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 1.6, color: '#aaddff', timer: 0.30 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.2, color: '#66aadd', timer: 0.55 });
      for (let p = 0; p < 8; p++) {
        const startX = cx + (Math.random() - 0.5) * CB * 0.7;
        const startY = cy - CB * (0.8 + Math.random() * 0.5);
        this.fx.push({
          type: 'particle',
          x: startX, y: startY,
          dx: (Math.random() - 0.5) * 8,
          dy: 130 + Math.random() * 60,
          color: DROP_COLORS[Math.floor(Math.random() * DROP_COLORS.length)],
          alpha: 1.4, timer: 0.45 + Math.random() * 0.2, size: 1.3 + Math.random() * 1.5,
        });
      }
    } else if (to === 'swamp') {
      // Muddy splatter — same palette as the Grow→swamp variant for
      // visual continuity; both Rain-on-grass and Grow-on-puddle land
      // on the same "this is now swamp" affordance.
      const MUD_COLORS = ['#6b5a2e', '#88a052', '#3d4a1f', '#aac070'];
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#88a052', timer: 0.35 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.1, color: '#6b5a2e', timer: 0.65 });
      for (let p = 0; p < 10; p++) {
        const ang = Math.random() * Math.PI * 2;
        const speed = 20 + Math.random() * 35;
        this.fx.push({
          type: 'particle',
          x: cx + (Math.random() - 0.5) * CB * 0.3,
          y: cy + (Math.random() - 0.5) * CB * 0.2,
          dx: Math.cos(ang) * speed,
          dy: Math.sin(ang) * speed * 0.6,
          color: MUD_COLORS[Math.floor(Math.random() * MUD_COLORS.length)],
          alpha: 1.4, timer: 0.65 + Math.random() * 0.30, size: 2 + Math.random() * 2,
        });
      }
    }
  }

  /**
   * Session 12 (Leader CCC, Rockfall) — predictive paint + falling-rock
   * visual. For each cell in `radius` of `tgt` (view coords), check the
   * local terrain and unit list:
   *   - If no alive unit on the cell, mirror the server's
   *     `_applyBoulderFall`: paint a 'boulder' on the cell now (so it
   *     visually appears at the spell-hit frame) and spawn dust + a
   *     dropping rock streak fx.
   *   - If an alive unit is on the cell, spawn a "shatter" burst —
   *     stone shards + impact ring + a brief dust cloud — without
   *     mutating terrain (the rock broke).
   * Base tiles are skipped — matches the server's defensive base guard.
   *
   * Trailing `terrainBoulderFall` re-asserts the paint authoritatively.
   */
  private _spawnRockfallFx(se: SpellCastRecord): void {
    if (!this.terrain) return;
    const tgt = se.tgt;
    const radius = se.sp.radius;
    const baseTiles = new Set<string>();
    for (const b of this.bases) {
      for (const t of b.tiles) baseTiles.add(`${t.x},${t.y}`);
    }
    // Session 12 follow-up — "had a unit at cast start" comes from the
    // impacts list. Each impact tells us a unit was on a Rockfall-
    // targeted cell (even if it just died). We look up the unit's
    // current pos in this.units, which is unchanged by death (only
    // totalSoldiers / dead flip), so it still gives the cell where the
    // rock shattered. Cells in this set DO NOT get a boulder.
    const shatteredCells = new Set<string>();
    for (const impact of se.impacts) {
      const u = this.units.find((x) => x.id === impact.unitId);
      if (!u) continue;
      shatteredCells.add(`${u.pos.x},${u.pos.y}`);
    }
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (baseTiles.has(`${x},${y}`)) continue;
        const cx = OXB + x * CB + CB / 2;
        const cy = OYB + y * CB + CB / 2;
        const isShattered = shatteredCells.has(`${x},${y}`);

        // Falling-rock streak — always plays so the player sees "stuff
        // dropped here". Heavy dark particles falling from above.
        for (let p = 0; p < 4; p++) {
          this.fx.push({
            type: 'particle',
            x: cx + (Math.random() - 0.5) * CB * 0.4,
            y: cy - CB * (1.4 + Math.random() * 0.6),
            dx: 0,
            dy: 200 + Math.random() * 80,
            color: Math.random() > 0.5 ? '#7a6650' : '#5a4730',
            alpha: 1.5, timer: 0.40, size: 3 + Math.random() * 1.5,
          });
        }

        if (isShattered) {
          // Shatter: stone shards fanning out + impact ring + dust cloud.
          // No terrain mutation — the rock broke on the standing unit.
          const SHARD_COLORS = ['#9a7a55', '#7a6650', '#5a4730', '#c8b395'];
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.8, color: '#c8b395', timer: 0.35 });
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 7, alpha: 1.3, color: '#7a6650', timer: 0.60 });
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 12, alpha: 0.8, color: '#5a4730', timer: 0.85 });
          for (let p = 0; p < 14; p++) {
            const ang = Math.random() * Math.PI * 2;
            const speed = 60 + Math.random() * 70;
            this.fx.push({
              type: 'particle',
              x: cx + (Math.random() - 0.5) * CB * 0.3,
              y: cy + (Math.random() - 0.5) * CB * 0.2,
              dx: Math.cos(ang) * speed,
              dy: Math.sin(ang) * speed * 0.7,
              color: SHARD_COLORS[Math.floor(Math.random() * SHARD_COLORS.length)],
              alpha: 1.5, timer: 0.55 + Math.random() * 0.30, size: 2 + Math.random() * 2.5,
            });
          }
        } else {
          // Empty cell: paint boulder NOW (client prediction). Settle
          // dust + small ring as the rock thuds into place.
          row[x] = 'boulder';
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.3, color: '#7a6650', timer: 0.50 });
          this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 0.7, color: '#5a4730', timer: 0.80 });
          for (let p = 0; p < 8; p++) {
            const ang = Math.PI * (0.85 + Math.random() * 0.3); // mostly outward + downward
            const speed = 25 + Math.random() * 35;
            this.fx.push({
              type: 'particle',
              x: cx + (Math.random() - 0.5) * CB * 0.5,
              y: cy + CB * 0.15,
              dx: Math.cos(ang) * speed,
              dy: -Math.sin(ang) * speed * 0.5,
              color: Math.random() > 0.5 ? '#9a8868' : '#6b5640',
              alpha: 1.2, timer: 0.55 + Math.random() * 0.20, size: 2 + Math.random() * 1.5,
            });
          }
        }
      }
    }
  }

  /**
   * Session 12 (Leader CCC counter) — predictive boulder-shatter visual
   * for Fire / Lightning damage with `breakBoulder: true`. For each
   * 'boulder' cell in radius, mutate terrain back to 'none' now (so the
   * cell visually clears at the spell-hit frame) and spawn dust + shards.
   * Trailing `terrainBoulderBreak` event re-asserts authoritatively.
   */
  private _spawnRockfallBreakFx(tgt: Pos, radius: number): void {
    if (!this.terrain) return;
    const SHARD_COLORS = ['#9a7a55', '#7a6650', '#c8b395', '#ddd0b0'];
    for (let dy = -radius; dy <= radius; dy++) {
      const remX = radius - Math.abs(dy);
      for (let dx = -remX; dx <= remX; dx++) {
        const x = tgt.x + dx;
        const y = tgt.y + dy;
        if (y < 0 || y >= this.terrain.length) continue;
        const row = this.terrain[y];
        if (!row || x < 0 || x >= row.length) continue;
        if (row[x] !== 'boulder') continue;
        row[x] = 'none';
        const cx = OXB + x * CB + CB / 2;
        const cy = OYB + y * CB + CB / 2;
        this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.7, color: '#ffe0a0', timer: 0.35 });
        this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 1.1, color: '#c8b395', timer: 0.65 });
        for (let p = 0; p < 12; p++) {
          const ang = Math.random() * Math.PI * 2;
          const speed = 50 + Math.random() * 60;
          this.fx.push({
            type: 'particle',
            x: cx + (Math.random() - 0.5) * CB * 0.3,
            y: cy + (Math.random() - 0.5) * CB * 0.2,
            dx: Math.cos(ang) * speed,
            dy: Math.sin(ang) * speed,
            color: SHARD_COLORS[Math.floor(Math.random() * SHARD_COLORS.length)],
            alpha: 1.4, timer: 0.50 + Math.random() * 0.30, size: 2 + Math.random() * 2,
          });
        }
      }
    }
  }

  /**
   * Session 11 follow-up — 淹水特效：Rain hits a unit already on a puddle.
   * Spawn a dramatic flood burst on top of the standard rain fx — bigger
   * concentric ripples, splashing droplets fanning outward, and a deep
   * blue ground tint. Stacks ON TOP of `_spawnRainFx`'s per-cell drizzle.
   */
  private _spawnFloodFx(pos: Pos): void {
    const cx = OXB + pos.x * CB + CB / 2;
    const cy = OYB + pos.y * CB + CB / 2;
    // Three concentric ripples — wider than the regular rain rings to read
    // as "flood, not drizzle". Inner is bright/cyan, outer is deep navy.
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 4, alpha: 2.0,
      color: '#ddf5ff', timer: 0.35,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 9, alpha: 1.6,
      color: '#66bbff', timer: 0.65,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 16, alpha: 1.2,
      color: '#3377cc', timer: 0.95,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 24, alpha: 0.7,
      color: '#1a4488', timer: 1.20,
    });
    // 12 splashing droplets fanning out along the ground plane (vy ≈ 0).
    const SPLASH_COLORS = ['#aaeeff', '#66aadd', '#ffffff', '#3388cc'];
    for (let p = 0; p < 12; p++) {
      const ang = (p / 12) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const speed = 70 + Math.random() * 55;
      this.fx.push({
        type: 'particle',
        x: cx + (Math.random() - 0.5) * CB * 0.25,
        y: cy + (Math.random() - 0.5) * CB * 0.25,
        dx: Math.cos(ang) * speed,
        dy: Math.sin(ang) * speed * 0.45 - 18,  // mostly horizontal, slight upward
        color: SPLASH_COLORS[Math.floor(Math.random() * SPLASH_COLORS.length)],
        alpha: 1.6,
        timer: 0.55 + Math.random() * 0.30,
        size: 2 + Math.random() * 2,
      });
    }
    // Extra heavy raindrops from above (denser than _spawnRainFx).
    for (let p = 0; p < 6; p++) {
      const startX = cx + (Math.random() - 0.5) * CB * 0.9;
      const startY = cy - CB * (1.0 + Math.random() * 0.5);
      this.fx.push({
        type: 'particle',
        x: startX, y: startY,
        dx: (Math.random() - 0.5) * 12,
        dy: 170 + Math.random() * 70,
        color: '#88ccee',
        alpha: 1.5,
        timer: 0.45 + Math.random() * 0.20,
        size: 1.8 + Math.random() * 1.6,
      });
    }
  }

  /**
   * Session 11 follow-up — 纏繞特效：Grow lands on a unit already standing
   * on grass (= the "root" branch). Spawn vines wrapping around the unit,
   * green tendril particles spiraling inward. Visually distinct from
   * `_spawnGrowFx`'s upward sprouts so the player can tell "I planted
   * more grass" from "I rooted an enemy in existing grass".
   */
  private _spawnEntangleFx(pos: Pos): void {
    const cx = OXB + pos.x * CB + CB / 2;
    const cy = OYB + pos.y * CB + CB / 2;
    // Tightening ring — starts wide, "pulses in" tonally via two rings.
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 12, alpha: 1.5,
      color: '#88dd44', timer: 0.40,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.8,
      color: '#446622', timer: 0.65,
    });
    // 10 vine tendril particles spiraling INWARD to the unit, tangential
    // velocity component to suggest wrapping. Tendrils take a moment
    // longer than sprout particles so they "linger and bind".
    const VINE_COLORS = ['#669933', '#88bb44', '#aaff66', '#446622'];
    for (let p = 0; p < 10; p++) {
      const ang = (p / 10) * Math.PI * 2;
      const startR = CB * 1.2;
      const px = cx + Math.cos(ang) * startR;
      const py = cy + Math.sin(ang) * startR;
      // Tangential + inward radial (wrapping spiral).
      const tangX = -Math.sin(ang);
      const tangY = Math.cos(ang);
      const radX = (cx - px) / startR;
      const radY = (cy - py) / startR;
      const speed = 60 + Math.random() * 35;
      this.fx.push({
        type: 'particle',
        x: px, y: py,
        dx: (tangX * 1.3 + radX * 1.2) * speed,
        dy: (tangY * 1.3 + radY * 1.2) * speed,
        color: VINE_COLORS[Math.floor(Math.random() * VINE_COLORS.length)],
        alpha: 1.7,
        timer: 0.65 + Math.random() * 0.25,
        size: 2 + Math.random() * 2,
      });
    }
    // Slash lines simulating thorny vines — 4 cross strokes through center.
    for (let s = 0; s < 4; s++) {
      const ang = (s / 4) * Math.PI + Math.PI / 8;
      const len = CB * 0.85;
      this.fx.push({
        type: 'slash',
        x1: cx - Math.cos(ang) * len, y1: cy - Math.sin(ang) * len,
        x2: cx + Math.cos(ang) * len, y2: cy + Math.sin(ang) * len,
        color: '#558833', alpha: 1.4, timer: 0.50,
      });
    }
  }

  /**
   * fix#5 — extra zap fx when Lightning hits a unit on a puddle. Spawn
   * a bright cyan shock-ring, 3 zigzag arc lines coming down onto the
   * cell, and a small cloud of cyan-white sparks. Stacks ON TOP of the
   * normal Lightning fx from `_renderSpellFx`.
   */
  private _spawnPuddleZapFx(pos: Pos): void {
    const cx = OXB + pos.x * CB + CB / 2;
    const cy = OYB + pos.y * CB + CB / 2;
    // Bright shock ring + outer dim ring
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 4, alpha: 2.0,
      color: '#ddf5ff', timer: 0.35,
    });
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: 6, alpha: 1.6,
      color: '#88ccff', timer: 0.55,
    });
    // 3 jagged arc slashes — diagonal bolt segments stitched onto the
    // cell. Each `slash` is 2 endpoints; we render 6 slashes total to
    // approximate a zigzag.
    for (let bolt = 0; bolt < 3; bolt++) {
      const baseAng = -Math.PI / 2 + (bolt - 1) * 0.5;
      let lastX = cx + Math.cos(baseAng) * CB * 0.9;
      let lastY = cy + Math.sin(baseAng) * CB * 0.9;
      const segs = 3;
      for (let i = 0; i < segs; i++) {
        const t = (i + 1) / segs;
        const r = (1 - t) * CB * 0.9;
        const ang = baseAng + (Math.random() - 0.5) * 0.9;
        const ex = cx + Math.cos(ang) * r;
        const ey = cy + Math.sin(ang) * r;
        this.fx.push({
          type: 'slash', x1: lastX, y1: lastY, x2: ex, y2: ey,
          color: bolt === 1 ? '#ffffff' : '#aaddff',
          alpha: 1.6, timer: 0.30,
        });
        lastX = ex; lastY = ey;
      }
    }
    // 10 spark particles
    for (let p = 0; p < 10; p++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 70;
      this.fx.push({
        type: 'particle',
        x: cx + (Math.random() - 0.5) * CB * 0.3,
        y: cy + (Math.random() - 0.5) * CB * 0.3,
        dx: Math.cos(ang) * speed,
        dy: Math.sin(ang) * speed * 0.7 - 25,
        color: p % 2 === 0 ? '#ffffff' : '#bbeeff',
        alpha: 1.8, timer: 0.45 + Math.random() * 0.25,
        size: 1.5 + Math.random() * 1.5,
      });
    }
  }

  /**
   * c 段 — push water-splash particles when a unit lands on a puddle.
   * Called from move-animation onComplete callbacks so the splash fires
   * as the unit visually arrives at the cell, not 0.5s before. `pos` is
   * in view coords (matches `Game.terrain`).
   */
  private _splashIfPuddle(pos: Pos): void {
    if (!this.terrain) return;
    const row = this.terrain[pos.y];
    if (!row) return;
    const cell = row[pos.x];
    if (cell !== 'puddle') return;
    const cx = OXB + pos.x * CB + CB / 2;
    const cy = OYB + pos.y * CB + CB / 2;
    // Soft splash ring
    this.fx.push({
      type: 'ring', x: cx, y: cy, radius: CB * 0.08,
      color: '#a8d4ff', alpha: 1.2, timer: 0.55,
    });
    // 6 droplets fanning out + up
    const drops = 6;
    for (let i = 0; i < drops; i++) {
      const ang = -Math.PI + (Math.PI * i) / (drops - 1); // -π .. 0 (upper half)
      const speed = 50 + Math.random() * 25;
      this.fx.push({
        type: 'particle',
        x: cx + Math.cos(ang) * 3,
        y: cy + Math.sin(ang) * 2,
        dx: Math.cos(ang) * speed,
        dy: Math.sin(ang) * speed - 30 - Math.random() * 20,
        color: i % 2 === 0 ? '#cce8ff' : '#7aaee0',
        alpha: 1.4,
        timer: 0.5 + Math.random() * 0.15,
        size: 1.5 + Math.random() * 1.2,
      });
    }
  }

  /** Render visual FX for a single spell entry */
  private _renderSpellFx(se: SpellCastRecord): void {
    const cx = OXB + se.tgt.x * CB + CB / 2;
    const cy = OYB + se.tgt.y * CB + CB / 2;
    const elName = ELEM[se.cmd.spellType];
    const color = ECOL[elName] || '#fff';
    const teamLabel = se.team === this.myTeam ? 'You' : (this.gameMode === 'single' ? 'AI' : 'Opp');

    // Spell name label with caster
    this.fx.push({
      type: 'floatText', x: cx, y: cy - 22,
      text: `${teamLabel}: ${SPELLS[elName]?.name || 'Spell'}`,
      color, alpha: 1.5, timer: 1.4, size: 14,
    });

    // Element-specific visual effects
    if (elName === 'Fire') {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#ff6600', timer: 0.7 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 1.0, color: '#ff4444', timer: 0.9 });
      for (let p = 0; p < 10; p++) {
        this.fx.push({
          type: 'particle', x: cx + (Math.random() - 0.5) * CB, y: cy + (Math.random() - 0.5) * CB,
          dx: (Math.random() - 0.5) * 40, dy: -60 - Math.random() * 80,
          color: Math.random() > 0.5 ? '#ff6600' : '#ffaa00', alpha: 1.5, timer: 0.7, size: 3 + Math.random() * 2,
        });
      }
    } else if (elName === 'Water') {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 1.2, color: '#44ccff', timer: 0.8 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 0.8, color: '#4499ff', timer: 1.0 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 18, alpha: 0.5, color: '#2266cc', timer: 1.2 });
    } else if (elName === 'Wind') {
      // Session 7 — Wind became Shield Wall (defense). FX evokes a
      // protective barrier: concentric green rings + vertical "wall"
      // sparks rising from the ground around each ally in radius.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.4, color: '#88ffbb', timer: 0.7 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 12, alpha: 1.0, color: '#33dd88', timer: 1.0 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 22, alpha: 0.6, color: '#22aa66', timer: 1.2 });
      for (let p = 0; p < 10; p++) {
        const ang = (p / 10) * Math.PI * 2;
        this.fx.push({
          type: 'particle',
          x: cx + Math.cos(ang) * CB * 0.8,
          y: cy + Math.sin(ang) * CB * 0.8,
          dx: 0, dy: -50 - Math.random() * 30,
          color: '#aaffcc', alpha: 1.3, timer: 0.7, size: 2.5,
        });
      }
    } else if (elName === 'Light') {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 2, alpha: 2.0, color: '#ffffaa', timer: 0.5 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.5, color: '#ffcc22', timer: 0.8 });
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 80, dy: Math.sin(angle) * 80,
          color: '#ffee66', alpha: 1.5, timer: 0.6, size: 3,
        });
      }
    } else if (elName === 'WindFriendly' || elName === 'WindHostile') {
      // Session 9 — whirlwind: swirling rings + tangential particles
      // spiralling inward to the spell center. Hues mirror ECOL:
      // friendly = lime, hostile = magenta (revision: previous teal/rose
      // were too close to Water / Fire on the color wheel).
      const ringCol = elName === 'WindFriendly' ? '#aaee44' : '#ee44bb';
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4,  alpha: 1.4, color: ringCol,  timer: 0.6 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 14, alpha: 1.0, color: ringCol,  timer: 0.9 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 26, alpha: 0.6, color: ringCol,  timer: 1.1 });
      // Tangential swirl — particles start on the rim of radius 2 cells
      // and curl inward with a tangential velocity component.
      const swirlR = CB * 1.8;
      for (let p = 0; p < 14; p++) {
        const ang = (p / 14) * Math.PI * 2;
        const px = cx + Math.cos(ang) * swirlR;
        const py = cy + Math.sin(ang) * swirlR;
        // Tangential (perpendicular to radial) + small inward radial.
        const tangX = -Math.sin(ang);
        const tangY = Math.cos(ang);
        const radX = (cx - px) / swirlR;
        const radY = (cy - py) / swirlR;
        const speed = 95 + Math.random() * 40;
        this.fx.push({
          type: 'particle',
          x: px, y: py,
          dx: (tangX + radX * 0.6) * speed,
          dy: (tangY + radY * 0.6) * speed,
          color: p % 2 === 0 ? ringCol : '#ffffff',
          alpha: 1.4,
          timer: 0.7 + Math.random() * 0.2,
          size: 2.5,
        });
      }
    } else if (elName === 'Grow') {
      // Session 11 (Leader CCC) — Grow casts a wide green burst at target
      // center. Per-cell sprout particles fire from _spawnGrowFx; this
      // is the spell-wide "you cast Grow" hit visual.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.6, color: '#ddff88', timer: 0.6 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 1.2, color: '#88dd44', timer: 0.85 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 20, alpha: 0.7, color: '#669933', timer: 1.05 });
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 70, dy: Math.sin(angle) * 70,
          color: '#aaff66', alpha: 1.4, timer: 0.55, size: 2.5,
        });
      }
    } else if (elName === 'Rain') {
      // Session 11 (Leader CCC) — Rain casts a soft blue burst at target
      // center. Per-cell raindrops fire from _spawnRainFx; this is the
      // spell-wide "you cast Rain" hit visual.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.5, color: '#aaddff', timer: 0.6 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 10, alpha: 1.1, color: '#66aadd', timer: 0.85 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 20, alpha: 0.7, color: '#3377aa', timer: 1.05 });
      // Falling streaks from above the target center.
      for (let p = 0; p < 8; p++) {
        const dx = (Math.random() - 0.5) * CB * 1.4;
        this.fx.push({
          type: 'particle',
          x: cx + dx, y: cy - CB * 1.4 + Math.random() * CB * 0.5,
          dx: 0, dy: 130 + Math.random() * 60,
          color: Math.random() > 0.5 ? '#aaddff' : '#ffffff',
          alpha: 1.4, timer: 0.5, size: 2,
        });
      }
    } else if (elName === 'Rockfall') {
      // Session 12 (Leader CCC) — Rockfall: brown/earth burst at target
      // center. Per-cell rock streaks fire from _spawnRockfallFx; this
      // is the spell-wide "you cast Rockfall" hit visual. Smaller rings
      // than Grow/Rain because the radius is 0 — visual reads as a
      // focused single-cell impact rather than an AOE.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 3, alpha: 1.8, color: '#c8b395', timer: 0.45 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 8, alpha: 1.3, color: '#9a7a55', timer: 0.75 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 14, alpha: 0.7, color: '#5a4730', timer: 1.00 });
      // Spell-wide impact crack: a few outward shards diverging from cy.
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 70, dy: Math.sin(angle) * 70,
          color: '#7a6650', alpha: 1.4, timer: 0.55, size: 2.5,
        });
      }
    } else if (elName === 'Lightning') {
      // Session 7 — Dark renamed to Lightning. FX is a downward strike:
      // bright top-down particle streak + sharp impact ring at target.
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 4, alpha: 1.8, color: '#ffffff', timer: 0.25 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 12, alpha: 1.4, color: '#dd99ff', timer: 0.55 });
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 26, alpha: 0.7, color: '#9944dd', timer: 0.9 });
      // Vertical strike bolt — fast falling particles from above the target
      for (let p = 0; p < 8; p++) {
        const dx = (Math.random() - 0.5) * 20;
        this.fx.push({
          type: 'particle',
          x: cx + dx, y: cy - CB * 1.6 + Math.random() * CB * 0.6,
          dx: 0, dy: 220 + Math.random() * 80,
          color: Math.random() > 0.5 ? '#ffffff' : '#dd88ff',
          alpha: 1.6, timer: 0.4, size: 2.5,
        });
      }
      // Outward sparks at impact
      for (let p = 0; p < 6; p++) {
        const angle = (p / 6) * Math.PI * 2;
        this.fx.push({
          type: 'particle', x: cx, y: cy,
          dx: Math.cos(angle) * 90, dy: Math.sin(angle) * 90,
          color: '#bb55ff', alpha: 1.3, timer: 0.45, size: 2.5,
        });
      }
    } else {
      this.fx.push({ type: 'ring', x: cx, y: cy, radius: 5, alpha: 1.2, color, timer: 0.8 });
    }

    // Session 10 — effects-list model: dispatch each impact by which
    // numeric field is set (one impact = one effect on one unit). A
    // single cast can now have multiple impacts per unit (e.g. Shield
    // Wall emits separate shieldPhys + shieldMag impacts; Whirlwind+heal
    // emits a heal impact alongside the move record).
    for (const impact of se.impacts) {
      const u = this.units.find(x => x.id === impact.unitId);
      if (!u || u.dead) continue;
      const ux = OXB + u.pos.x * CB + CB / 2;
      const uy = OYB + u.pos.y * CB + CB / 2;

      if (impact.dmg !== undefined && impact.dmg > 0) {
        u.flash = 0.4; u.flashColor = color;
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: '-' + impact.dmg,
          color: '#ff4444', alpha: 1.5, timer: 1.0, size: 14,
        });
      } else if (impact.heal !== undefined && impact.heal > 0) {
        u.flash = 0.3; u.flashColor = '#44ff88';
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: '+' + impact.heal,
          color: '#44ff88', alpha: 1.5, timer: 1.0, size: 14,
        });
      } else if (impact.buff !== undefined && impact.buff !== 0) {
        // Positive = buff (Bless), negative = debuff (Whirlwind hostile).
        const isDebuff = impact.buff < 0;
        u.flash = 0.3; u.flashColor = isDebuff ? '#ee44bb' : '#ffcc22';
        const sign = isDebuff ? '' : '+';   // negative already has '−'
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: `ATK${sign}${impact.buff}`,
          color: isDebuff ? '#ee44bb' : '#ffcc22', alpha: 1.5, timer: 1.0, size: 12,
        });
      } else if (impact.defPhys !== undefined && impact.defPhys > 0) {
        // Shield Wall — physical leg.
        u.flash = 0.3; u.flashColor = '#88ffbb';
        const pct = Math.round(impact.defPhys * 100);
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: `SHIELD ${pct}%`,
          color: '#88ffbb', alpha: 1.5, timer: 1.0, size: 12,
        });
      } else if (impact.defMag !== undefined && impact.defMag > 0) {
        // Shield Wall — magic leg. Position slightly below the phys label
        // so co-cast (Wind = phys + mag in same cast) reads as two lines.
        u.flash = 0.3; u.flashColor = '#88ffbb';
        const pct = Math.round(impact.defMag * 100);
        this.fx.push({
          type: 'floatText', x: ux, y: uy + 4,
          text: `MAGRES ${pct}%`,
          color: '#88ffbb', alpha: 1.4, timer: 1.0, size: 11,
        });
      } else if (impact.rooted !== undefined && impact.rooted > 0) {
        // Session 11 (Leader CCC) — Grow root: vines wrap the target.
        // Color matches Grow's ECOL (#669933) but a touch brighter so it
        // reads on dark unit bodies.
        u.flash = 0.35; u.flashColor = '#669933';
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: '🌿 ROOTED',
          color: '#aaff66', alpha: 1.5, timer: 1.1, size: 12,
        });
        // 纏繞特效：spawn extra vines around the rooted unit (Session 11
        // follow-up). The rooting is the "on existing grass" branch, so
        // this fires whenever Grow lands on a grass-standing enemy.
        this._spawnEntangleFx(u.pos);
      } else if (impact.defDown !== undefined && impact.defDown > 0) {
        // Session 11 follow-up — Grow "open ground" branch: defense down.
        // Reddish-brown floattext (vulnerability hue) so it's not confused
        // with the green rooted label.
        u.flash = 0.35; u.flashColor = '#cc6644';
        const pct = Math.round(impact.defDown * 100);
        this.fx.push({
          type: 'floatText', x: ux, y: uy - 10,
          text: `DEF -${pct}%`,
          color: '#ff9966', alpha: 1.5, timer: 1.1, size: 12,
        });
      }
    }

    // Session 9/10 — whirlwind: per-displaced-unit "swirl" label.
    // The pull effect itself doesn't emit impacts (it emits moves), so
    // we still iterate se.moves to put a visual confirmation on each
    // pulled unit. Color matches the element ECOL palette.
    if (Array.isArray(se.moves) && se.moves.length > 0) {
      const labelCol = elName === 'WindFriendly' ? '#aaee44'
                     : elName === 'WindHostile' ? '#ee44bb'
                     : '#ffffff';
      for (const m of se.moves) {
        const u = this.units.find((x) => x.id === m.unitId);
        if (!u || u.dead) continue;
        u.flash = 0.25; u.flashColor = labelCol;
        const tx = OXB + m.to.x * CB + CB / 2;
        const ty = OYB + m.to.y * CB + CB / 2;
        this.fx.push({
          type: 'floatText', x: tx, y: ty - 10,
          text: '旋',
          color: labelCol, alpha: 1.4, timer: 0.8, size: 14,
        });
      }
    }
  }

  _teamHP(team: number): { healthy: number; total: number } {
    let healthy = 0, total = 0;
    for (const u of this.units) {
      if (u.dead || u.team !== team) continue;
      healthy += u.hp;
      total += u.totalSoldiers;
    }
    return { healthy, total };
  }

  /**
   * Session 10 follow-up — client-side equivalent of the server's
   * end-of-tick modifier decrement loop. Called once per tick boundary
   * crossed (between `tickLabel` events, and one final time at
   * `_finishExec` for the last tick). Keeps Game.units' modifier list
   * (used by the Renderer for badges) aligned with server's lifecycle.
   */
  private _decrementUnitModifiers(): void {
    for (const u of this.units) {
      if (!u.modifiers || u.modifiers.length === 0) continue;
      let expired = false;
      for (let i = u.modifiers.length - 1; i >= 0; i--) {
        u.modifiers[i].ticksRemaining -= 1;
        if (u.modifiers[i].ticksRemaining <= 0) {
          u.modifiers.splice(i, 1);
          expired = true;
        }
      }
      if (expired) _recomputeScalars(u);
    }
  }

  private _finishExec(): void {
    this.phaseBanner = null;
    this.stepActive = false;
    // Session 10 follow-up — final tick's end-of-tick decrement (server
    // does it after Step F of the last tick before returning the events
    // list; we mirror it here so badges expire visually at the right time).
    this._decrementUnitModifiers();
    const t0 = this.units.filter(u => !u.dead && u.team === 0).length;
    const t1 = this.units.filter(u => !u.dead && u.team === 1).length;
    const b0Dead = this.bases.find(b => b.team === 0)?.destroyed ?? false;
    const b1Dead = this.bases.find(b => b.team === 1)?.destroyed ?? false;
    if (t0 === 0 || t1 === 0 || b0Dead || b1Dead || this.round >= this.maxRounds) {
      this._endGame();
    } else {
      this._startRound();
    }
  }

  private _endGame(): void {
    this.state = GS.OVER;
    const hp0 = this._teamHP(0).healthy;
    const hp1 = this._teamHP(1).healthy;
    const b0Dead = this.bases.find(b => b.team === 0)?.destroyed ?? false;
    const b1Dead = this.bases.find(b => b.team === 1)?.destroyed ?? false;
    const t0Dead = this.units.filter(u => !u.dead && u.team === 0).length === 0;
    const t1Dead = this.units.filter(u => !u.dead && u.team === 1).length === 0;

    // Win conditions: enemy units all dead OR enemy base destroyed
    const p0Lost = t0Dead || b0Dead;
    const p1Lost = t1Dead || b1Dead;

    if (p0Lost && p1Lost) this.winner = -1;
    else if (p1Lost) this.winner = 0;
    else if (p0Lost) this.winner = 1;
    else this.winner = hp0 >= hp1 ? 0 : 1;

    // Story Mode — switch the controller to post / lose script. The
    // dialogue overlay will block input on OVER until the player
    // dismisses it, then `Renderer._gameOver` shows Play Again / Retry.
    if (this.storyController) {
      this.storyController.onGameOver(this.winner, this.myTeam);
    }
  }

  restart(): void {
    anim.clear();
    // Close any live network connection before tearing down the battle.
    if (this.adapter && (this.adapter as any).disconnect) {
      try { (this.adapter as any).disconnect(); } catch { /* ignore */ }
    }
    // Phase 3d Step 3: drop any lobby event subscriptions.
    for (const u of this._lobbyUnsubs) { try { u(); } catch { /* ignore */ } }
    this._lobbyUnsubs = [];
    this.units = [];
    this.bases = [];
    this.board = new M3Board();
    this.aiBoard = new M3Board();
    this.board._onMatch = (e) => this._onPlayerMatch(e);
    this.board._onChainComplete = (e) => this._onPlayerChainComplete(e);
    this.aiBoard._onMatch = (e) => this._onAIMatch(e);
    this.aiBoard._onChainComplete = (e) => this._onAIChainComplete(e);
    this.round = 0;
    this.fx = [];
    this.winner = -1;
    this.events = [];
    // Phase 3d Step 3: reset PvP plumbing so next session starts clean.
    this.gameMode = 'single';
    this.myTeam = 0;
    this.roomCode = null;
    this.lobbyMessage = '';
    this.lobbyPending = false;
    this.opponentSessionId = null;
    // Phase 3d Step 4: clear waiting flags too.
    this.waitingForOpponent = false;
    this.awaitingBattleReady = false;
    // Phase 3d Step 5: clear opponent-left flag.
    this.opponentLeft = false;
    // Mirror flag for our own disconnect — cleared on restart so a fresh
    // session doesn't inherit the "(連線中斷)" subtitle.
    this.selfDisconnected = false;
    this.deployDeadline = null;
    // Terrain: clear so the next session re-rolls in startSinglePlayer /
    // startCreateRoom / startJoinRoom.
    this.terrain = null;
    this.terrainSeed = 0;
    // Story Mode — drop the controller so Play Again returns to MENU /
    // STORY_SELECT instead of replaying the same scripted chapter.
    this.storyController = null;
    this._storySettlePending = false;
    // Casual Mode — drop the active level so Play Again returns to
    // MENU. `exitToCasualSelect` / `retryCasualLevel` re-set this if
    // the player chose those branches before calling restart().
    this.casualLevel = null;
    // Scripted-mode AI roster — only relevant inside an active story/
    // casual session. Cleared so a fresh single/PvP run doesn't see
    // stale preset AI.
    this._presetAiUnits = null;
    // Deployment rules — reset to default manual so the next single /
    // PvP `start*` entry inherits a clean slate; scripted entries
    // re-set to PRESET_RULES themselves. setRules also resets
    // deployment.leader to DEFAULT_LEADER_ID.
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    // Session 9 — clear per-team leader snapshots; startBattle will
    // re-fill them from the next session's deployment + opponent.
    this.playerLeader = DEFAULT_LEADER_ID;
    this.aiLeader = DEFAULT_LEADER_ID;
    // Surrender — reset in case we tear down mid-hold or mid-modal.
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
    this.surrenderConfirmActive = false;
    resetId();
    this.state = GS.MENU;
  }

  // ----------------------------------------------------------
  // Phase 3d Step 3 — Lobby / room flow
  // ----------------------------------------------------------

  /**
   * Start as the host: build the network adapter, ask server for a
   * fresh room code, then sit in the LOBBY state until the guest joins.
   * Single-player or non-net Mode shouldn't call this (UI guards it).
   */
  async startCreateRoom(): Promise<void> {
    if (this.state !== GS.MENU) return;
    if (this.lobbyPending) return;
    if (!this.netMode) {
      console.warn('[game] startCreateRoom called outside net mode');
      return;
    }
    this.lobbyPending = true;
    this.adapter = new NetworkServerAdapter(this.netUrl);
    this.gameMode = 'pvp-host';
    // PvP currently always uses the default manual rule (5v5 standard).
    // Future PvP-mode picker will let the host choose; server then sends
    // the same rule id to the guest via createRoom RPC.
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    this.state = GS.LOBBY;
    this.lobbyMessage = 'Connecting...';
    try {
      const res = await this.adapter.createRoom();
      this.roomCode = res.roomCode;
      this.myTeam = res.myTeam;
      // Terrain: server picked a seed at room creation. myTeam = 0 here
      // (host), so view == world; no flip needed.
      this.terrainSeed = res.terrainSeed;
      this.terrain = generateTerrain(this.terrainSeed);
      this.lobbyMessage = 'Room: ' + res.roomCode + ' — waiting for opponent...';
      // Subscribe to opponentJoined push so we can advance to deployment.
      const unsub = this.adapter.onEvent('opponentJoined', (data: any) => {
        this.opponentSessionId = data?.opponentSessionId ?? null;
        this.deployDeadline = data?.deployDeadline ?? null;
        this.lobbyMessage = 'Opponent joined! Loading deployment...';
        // Brief pause so the user reads the message, then go to deploy.
        setTimeout(() => {
          if (this.state === GS.LOBBY) this.startDeployment();
        }, 600);
      });
      this._lobbyUnsubs.push(unsub);
      this._installPvpBattleSubscribers();
    } catch (err) {
      this.lobbyMessage = 'Create room failed: ' + (err as Error).message;
      console.error('[game] createRoom failed:', err);
    } finally {
      this.lobbyPending = false;
    }
  }

  /**
   * Join an existing room as guest. Caller must provide a room code.
   * Single-player or non-net mode shouldn't call this (UI guards it).
   */
  async startJoinRoom(code: string): Promise<void> {
    if (this.state !== GS.MENU) return;
    if (this.lobbyPending) return;
    if (!this.netMode) {
      console.warn('[game] startJoinRoom called outside net mode');
      return;
    }
    if (!code || code.trim().length === 0) {
      console.warn('[game] startJoinRoom called with empty code');
      return;
    }
    this.lobbyPending = true;
    this.adapter = new NetworkServerAdapter(this.netUrl);
    this.gameMode = 'pvp-guest';
    // Guest mirrors host's rules. Currently hardcoded to DEFAULT_MANUAL_RULES;
    // future server-driven mode picker will set this from joinRoom RPC reply.
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    this.state = GS.LOBBY;
    this.lobbyMessage = 'Joining ' + code + '...';
    try {
      const res = await this.adapter.joinRoom(code.trim().toUpperCase());
      this.roomCode = res.roomCode;
      this.myTeam = res.myTeam;
      // Terrain: server's room-level seed (same as host got). myTeam = 1
      // here (guest), so generate in world coords then flip for view.
      this.terrainSeed = res.terrainSeed;
      this.deployDeadline = res.deployDeadline ?? null;
      this.terrain = flipTerrainForView(generateTerrain(this.terrainSeed));
      this.lobbyMessage = 'Joined ' + res.roomCode + '! Loading deployment...';
      this._installPvpBattleSubscribers();
      // Joiner advances to deployment immediately — pairing is already done.
      setTimeout(() => {
        if (this.state === GS.LOBBY) this.startDeployment();
      }, 400);
    } catch (err) {
      this.lobbyMessage = 'Join failed: ' + (err as Error).message;
      this.state = GS.MENU; // bounce back so user can try a different code
      this.gameMode = 'single';
      console.error('[game] joinRoom failed:', err);
      // Also tear down the half-open adapter to free the socket.
      try { (this.adapter as any).disconnect?.(); } catch { /* ignore */ }
    } finally {
      this.lobbyPending = false;
    }
  }

  /** Start a single-player session (legacy MENU tap behavior).
   *  Single = offline PvP variant — uses the same DEFAULT_MANUAL_RULES
   *  as PvP, so AI mirrors player composition (5 units each). Future
   *  3v3 / 10v10 modes will be addable via a different rule selection. */
  startSinglePlayer(): void {
    if (this.state !== GS.MENU) return;
    this.gameMode = 'single';
    this.deployment.setRules(DEFAULT_MANUAL_RULES);
    this.myTeam = 0;
    this.roomCode = null;
    this.deployDeadline = null;
    // Terrain: pick a seed locally so it can render during DEPLOY. The
    // same seed is shipped to the server in `startBattle` -> initBattle
    // so authoritative effect resolution lands on the same grid.
    // myTeam = 0 in single, so view == world; no flip needed.
    this.terrainSeed = randomSeed();
    this.terrain = generateTerrain(this.terrainSeed);
    this.startDeployment();
  }

  // ----------------------------------------------------------
  // STORY MODE — chapter picker + chapter bootstrap
  // ----------------------------------------------------------

  /**
   * MENU → STORY_SELECT transition. Wired to the "Story Mode" button.
   * The chapter list is `CHAPTERS` from `./story/StoryData.ts`.
   * Renderer._storySelect draws the list, Input routes taps back
   * here to `startStoryChapter(id)`.
   */
  startStoryMode(): void {
    if (this.state !== GS.MENU) return;
    this.state = GS.STORY_SELECT;
  }

  /** List of all registered chapters (Renderer + Input read this). */
  listChapters(): ChapterDef[] {
    return CHAPTERS;
  }

  /**
   * STORY_SELECT → DEPLOY transition. Boots up a StoryController for
   * the chosen chapter, runs the pre-battle dialogue, then auto-starts
   * battle once the script reaches a MATCH3-phase step.
   *
   * Story mode uses single-player wire path (LocalServerAdapter), with
   * a fixed deterministic terrain seed so designers can reason about
   * grass / puddle placement in the script.
   */
  startStoryChapter(chapterId: string): void {
    // Allow re-entry from OVER (Retry-after-lose flow goes through here too).
    if (this.state !== GS.STORY_SELECT && this.state !== GS.MENU && this.state !== GS.OVER) return;
    const chapter = getChapter(chapterId);
    if (!chapter) {
      console.warn('[game] unknown chapter id:', chapterId);
      return;
    }
    this.gameMode = 'story';
    // Story is a scripted mode — preset rules disable manual placement
    // UI (palette / save slots / drag-remove) and lift composition caps
    // so chapter scripts can pre-place any unit roster they need.
    this.deployment.setRules(PRESET_RULES);
    this.myTeam = 0;
    this.roomCode = null;
    this.deployDeadline = null;
    // Fixed terrain seed for story — deterministic so scripts can
    // reference specific tiles. Different chapters can override this
    // later via a `chapter.terrainSeed` field if needed.
    this.terrainSeed = 0x4D52_0001; // 'MR\x00\x01' loosely = Maroon5 chapter 1
    this.terrain = generateTerrain(this.terrainSeed);
    // Instantiate + start the controller. `start()` immediately walks
    // the script forward to the first dialogue / pending action.
    this.storyController = new StoryController(chapter);
    this.storyController.start();
    this.startDeployment();
  }

  // ----------------------------------------------------------
  // Surrender flow — long-press a top-right button in MATCH3 for 5s,
  // confirm in a modal, then either bail to MENU (story) or force-end
  // the battle as a loss (single / PvP).
  // ----------------------------------------------------------

  /** Called from Input._down when the surrender button is pressed. */
  startSurrenderHold(): void {
    if (this.state !== GS.MATCH3) return;
    if (this.surrenderConfirmActive) return;
    this.surrenderHolding = true;
    this.surrenderHoldTime = 0;
  }

  /** Called when the press is released / drifts off the button / phase
   *  changes. Resets progress without showing the modal. */
  cancelSurrenderHold(): void {
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
  }

  /** Dismiss the confirm modal without surrendering (user picked "取消"). */
  dismissSurrenderConfirm(): void {
    this.surrenderConfirmActive = false;
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
  }

  /** User confirmed surrender — branch on game mode. */
  confirmSurrender(): void {
    if (!this.surrenderConfirmActive) return;
    this.surrenderConfirmActive = false;
    this.surrenderHolding = false;
    this.surrenderHoldTime = 0;
    if (this.gameMode === 'story') {
      // Story mode: bail straight to MENU. No lose-script dialogue, no
      // OVER screen — user explicitly asked to skip the post-story
      // dialogue when surrendering a tutorial.
      this.restart();
      return;
    }
    if (this.gameMode === 'casual') {
      // Casual mode: bail back to the level picker. No OVER screen since
      // the player explicitly abandoned the level; treat like a soft exit.
      this.exitToCasualSelect();
      return;
    }
    // Single / PvP: force a defeat, show GameOver screen.
    if (!this._isLocalMode() && this.adapter) {
      // PvP: drop the socket so the opponent receives `opponentDisconnected`
      // and auto-wins on their side (matches the existing disconnect flow).
      try { (this.adapter as any).disconnect?.(); } catch { /* ignore */ }
    }
    this.state = GS.OVER;
    this.winner = (this.myTeam === 0 ? 1 : 0);
  }

  /** Retry the current story chapter from the start (Lose → Retry flow). */
  retryStoryChapter(): void {
    if (!this.storyController) return;
    const chapterId = this.storyController.chapter.id;
    // Tear down current battle state via the standard restart path, then
    // boot a fresh controller for the same chapter.
    this.restart();
    this.startStoryChapter(chapterId);
  }

  /** Return to the chapter list (used after winning a story chapter). */
  exitToStorySelect(): void {
    this.restart();
    this.state = GS.STORY_SELECT;
  }

  // ----------------------------------------------------------
  // CASUAL MODE — level picker + level bootstrap
  // ----------------------------------------------------------

  /**
   * MENU → CASUAL_SELECT transition. Wired to the "Casual Mode" button.
   * The level list comes from `CASUAL_LEVELS` in `./casual/CasualLevels.ts`.
   * Renderer._casualSelect draws the list; Input routes taps back here
   * to `startCasualLevel(id)`.
   */
  startCasualMode(): void {
    if (this.state !== GS.MENU) return;
    this.state = GS.CASUAL_SELECT;
  }

  /** Snapshot of all registered casual levels (Renderer + Input read this). */
  listCasualLevels(): CasualLevelDef[] {
    return CASUAL_LEVELS;
  }

  /**
   * CASUAL_SELECT → DEPLOY transition. Loads the chosen level's preset
   * units onto the deployment screen so the player can edit paths, then
   * defers to the standard DEPLOY → MATCH3 → ... pipeline.
   *
   * Casual mode reuses the single-player wire path (LocalServerAdapter)
   * and the standard single-player AI. The only differences from
   * gameMode='single' are:
   *   - units on both sides come from the level data (no random AI
   *     deployment, no palette drag-drop)
   *   - terrain comes from the level's explicit grid (overrideTerrain
   *     in startBattle bypasses generateTerrain)
   *   - DEPLOY UI is restricted (Input/Renderer gate placement /
   *     removal / save-slot / palette tap)
   */
  startCasualLevel(levelId: string): void {
    // Allow re-entry from OVER (Retry / next-level flow goes through here).
    if (this.state !== GS.CASUAL_SELECT && this.state !== GS.MENU && this.state !== GS.OVER) return;
    const level = getCasualLevel(levelId);
    if (!level) {
      console.warn('[game] unknown casual level id:', levelId);
      return;
    }
    this.gameMode = 'casual';
    // Casual is a scripted mode — preset rules disable manual placement
    // UI and lift composition caps so levels can ship any roster size
    // (e.g. lvl 5 has 8 player units, way over the standard cap of 5).
    this.deployment.setRules(PRESET_RULES);
    this.myTeam = 0;
    this.roomCode = null;
    this.deployDeadline = null;
    this.casualLevel = level;
    // Build the explicit terrain grid up-front so DEPLOY can reveal it
    // for path planning. `startBattle` re-builds the same grid and calls
    // `overrideTerrain` to feed the server-authoritative copy.
    const grid = buildTerrainGrid(level);
    this.terrain = grid;
    // Mark as 0 so anything inspecting terrainSeed knows the grid is
    // hand-authored rather than derived from a seed.
    this.terrainSeed = 0;
    this.startDeployment();
  }

  /** Retry the current casual level from the start (Lose → Retry flow). */
  retryCasualLevel(): void {
    if (!this.casualLevel) return;
    const levelId = this.casualLevel.id;
    this.restart();
    this.startCasualLevel(levelId);
  }

  /** Return to the level list (used after winning a casual level). */
  exitToCasualSelect(): void {
    this.restart();
    this.state = GS.CASUAL_SELECT;
  }

  /**
   * Phase 5a Step 1 — open a browser prompt to edit the WebSocket
   * server URL. Triggered by the "Server" menu button when net mode
   * is on. Empty input clears the saved override (revert to build
   * env / fallback). Invalid input (non ws://) shows an alert and
   * leaves the URL unchanged. The new URL is persisted to
   * localStorage so the next session boots with it.
   *
   * Note: the new URL takes effect on the *next* createRoom /
   * joinRoom call. If the user has already opened an adapter
   * (lobby flow), they need to Cancel back to MENU first.
   */
  promptServerUrl(): void {
    if (typeof window === 'undefined') return;
    const cur = this.netUrl;
    void showPrompt(
      'Server WebSocket URL\n(must start with ws:// or wss://)\nLeave blank to reset to default.',
      cur,
    ).then((input) => {
      if (input === null) return; // user cancelled
      const trimmed = input.trim();
      if (trimmed === '') {
        clearNetUrl();
        this.netUrl = resolveNetUrl();
        console.log(`[game] server URL reset → ${this.netUrl}`);
        return;
      }
      try {
        saveNetUrl(trimmed);
        this.netUrl = trimmed;
        console.log(`[game] server URL set → ${this.netUrl}`);
      } catch (err) {
        console.warn('[game] invalid server URL:', (err as Error).message);
      }
    });
  }

  /**
   * Phase 3d Step 4 — wire up the PvP-only push events. Called once
   * per session (from startCreateRoom / startJoinRoom) right after the
   * adapter is constructed.
   *
   * Events handled:
   *   - battleReady:   server has both initBattle payloads, can start round 1
   *   - execComplete:  server ran exec, here are the events to play back
   */
  private _installPvpBattleSubscribers(): void {
    if (!this.adapter) return;

    const readyUnsub = this.adapter.onEvent('battleReady', (data: any) => {
      console.log('[game] battleReady received — units=' +
        (Array.isArray(data?.units) ? data.units.length : '?') +
        ' bases=' + (Array.isArray(data?.bases) ? data.bases.length : '?'));
      if (!this.awaitingBattleReady) {
        console.warn('[game] battleReady received but not expecting it; ignoring');
        return;
      }
      // Phase 3d Step 4 fix: replace local units/bases with the server's
      // merged authoritative versions so we can render BOTH teams.
      // unitFromJson reattaches the hp/atk getters lost in JSON transit.
      // Then flip world→view if we're the team-1 player (guest).
      if (Array.isArray(data?.units)) {
        this.units = data.units.map((u: any) => {
          const unit = unitFromJson(u);
          if (this.viewFlipped) {
            unit.pos = this._flipPos(unit.pos);
            unit.vx = unit.pos.x;
            unit.vy = unit.pos.y;
            unit.path = unit.path.map((p) => this._flipPos(p));
          }
          return unit;
        });
      }
      if (Array.isArray(data?.bases)) {
        this.bases = data.bases.map((b: any) => {
          const base = baseFromJson(b);
          if (this.viewFlipped) {
            base.tiles = base.tiles.map((t) => this._flipPos(t));
          }
          return base;
        });
      }
      // Terrain: server ships its authoritative grid + seed. Override the
      // client's local copy so any future divergence is reconciled here at
      // the wire boundary (same flip-for-view rule as units / bases).
      if (Array.isArray(data?.terrain)) {
        const worldTerrain = data.terrain as TerrainGrid;
        this.terrain = this.viewFlipped
          ? flipTerrainForView(worldTerrain)
          : worldTerrain.map((row) => row.slice());
      }
      if (typeof data?.terrainSeed === 'number') {
        this.terrainSeed = data.terrainSeed >>> 0;
      }
      this.awaitingBattleReady = false;
      this.waitingForOpponent = false;
      if (this.state !== GS.DEPLOY && this.state !== GS.LOBBY) {
        console.warn('[game] battleReady arrived in unexpected state:', this.state);
        return;
      }
      this._startRound();
    });
    this._lobbyUnsubs.push(readyUnsub);

    const leftUnsub = this.adapter.onEvent('opponentDisconnected', (data: any) => {
      console.log('[game] opponentDisconnected received', data);
      this._handleOpponentLeft();
    });
    this._lobbyUnsubs.push(leftUnsub);

    // Synthetic event emitted by NetworkServerAdapter.onclose when WE
    // lose the socket unexpectedly (network drop / server kill / mobile
    // backgrounding). Without this, the disconnected side would sit
    // frozen behind the "Waiting for opponent" overlay because no server
    // push will ever arrive to clear it. See `_handleConnectionLost`.
    const lostUnsub = this.adapter.onEvent('connectionLost', (data: any) => {
      console.log('[game] connectionLost received', data);
      this._handleConnectionLost();
    });
    this._lobbyUnsubs.push(lostUnsub);

    const execUnsub = this.adapter.onEvent('execComplete', (data: any) => {
      console.log('[game] execComplete received — events=' +
        (Array.isArray(data?.events) ? data.events.length : '?'));
      const rawEvents = Array.isArray(data?.events) ? data.events : [];
      // Phase 3d Step 4 fix: flip event positions for guest's view.
      this.events = this._flipEventsForView(rawEvents);
      this.execPending = false;
      this.waitingForOpponent = false;
      this.stepActive = true;
    });
    this._lobbyUnsubs.push(execUnsub);
  }

  /**
   * Phase 3d Step 4 fix: convert a SpellCommand whose targetPos is in
   * VIEW coords (as stored client-side) into WORLD coords (as expected
   * by the server). Identity when viewFlipped is false.
   */
  private _cmdViewToWorld(cmd: SpellCommand | null): SpellCommand {
    if (!cmd) return { slotIndex: 0, spellType: 0, powerLevel: 0 };
    if (!cmd.targetPos) return cmd;
    return {
      ...cmd,
      targetPos: this._flipPos(cmd.targetPos),
    };
  }

  /**
   * Phase 3d Step 5 — handle the server's opponentDisconnected push.
   *
   * Rule: if the battle has actually started (MATCH3/SCHED/EXEC), award
   * the local player the win and end the game. If we never got past
   * deployment (DEPLOY / pre-battle LOBBY), bounce back to MENU since
   * "winning" without a fight would be weird.
   *
   * Idempotent: subsequent calls (e.g. duplicate pushes) are no-ops.
   */
  private _handleOpponentLeft(): void {
    if (this.opponentLeft) return;
    this.opponentLeft = true;
    this.adapterError = 'Opponent disconnected';

    const inBattle =
      this.state === GS.MATCH3 ||
      this.state === GS.SCHED ||
      this.state === GS.EXEC;

    if (inBattle) {
      // Concede on the opponent's behalf; we win.
      this.winner = this.myTeam;
      this.waitingForOpponent = false;
      this.execPending = false;
      this.stepActive = false;
      // Drop any in-flight playback so we land cleanly on the OVER screen.
      this.events = [];
      this.fx = [];
      try { anim.clear(); } catch { /* ignore */ }
      this.state = GS.OVER;
      return;
    }

    // Pre-battle (MENU / LOBBY / DEPLOY): force LOBBY state so the user
    // SEES the disconnect message (DEPLOY screen doesn't render
    // lobbyMessage). Then bounce to MENU after a beat.
    this.lobbyMessage = 'Opponent disconnected. Returning to menu...';
    this.awaitingBattleReady = false;
    this.waitingForOpponent = false;
    this.state = GS.LOBBY;
    setTimeout(() => {
      // Only bounce if we're still in LOBBY — user might have already
      // navigated themselves (e.g. clicked Cancel).
      if (this.state === GS.LOBBY) this.restart();
    }, 1800);
  }

  /**
   * Handle OUR socket closing unexpectedly while a PvP session was live.
   * Mirror of `_handleOpponentLeft` for the disconnected side — the
   * server (and the other player) will see the equivalent
   * `opponentDisconnected` push; we synthesise this event locally so
   * the disconnected client doesn't sit frozen on "Waiting for opponent".
   *
   * Rule: if the battle has actually started (MATCH3/SCHED/EXEC), the
   * local player concedes (we know the opponent will be awarded the win
   * server-side). If we never got past deployment (DEPLOY / pre-battle
   * LOBBY), bounce back to MENU since "losing" without a fight would be
   * weird.
   *
   * Idempotent: subsequent calls (e.g. double-fire from queued RPC
   * rejections triggering another path) are no-ops.
   *
   * Skipped when:
   *   - Local mode (single / story) — no real socket, shouldn't trigger
   *   - Already on OVER or MENU — surrender / opponentLeft already
   *     resolved the session; don't clobber their result
   */
  private _handleConnectionLost(): void {
    if (this.selfDisconnected) return;
    if (this._isLocalMode()) return;
    if (this.state === GS.OVER || this.state === GS.MENU) return;

    this.selfDisconnected = true;
    this.adapterError = 'Connection lost';

    const inBattle =
      this.state === GS.MATCH3 ||
      this.state === GS.SCHED ||
      this.state === GS.EXEC;

    if (inBattle) {
      // We lost the connection → server will award the opponent the win
      // (or has already). Mirror that locally so the OVER screen reads
      // DEFEAT with a "(連線中斷)" subtitle (see Renderer._gameOver).
      this.winner = (this.myTeam === 0 ? 1 : 0);
      this.waitingForOpponent = false;
      this.execPending = false;
      this.stepActive = false;
      // Drop any in-flight playback so we land cleanly on the OVER screen.
      this.events = [];
      this.fx = [];
      try { anim.clear(); } catch { /* ignore */ }
      this.state = GS.OVER;
      return;
    }

    // Pre-battle (LOBBY / DEPLOY): force LOBBY state so the user SEES
    // the disconnect message (DEPLOY screen doesn't render
    // lobbyMessage). Then bounce to MENU after a beat — matches the
    // pre-battle path of `_handleOpponentLeft`.
    this.lobbyMessage = '連線中斷，返回主選單...';
    this.awaitingBattleReady = false;
    this.waitingForOpponent = false;
    this.state = GS.LOBBY;
    setTimeout(() => {
      if (this.state === GS.LOBBY) this.restart();
    }, 1800);
  }

  /**
   * Phase 3e — Client-side prediction.
   *
   * Pre-3e behaviour: every swap awaited `adapter.requestSwap` on the
   * wire (~80ms RTT on fly.dev / nrt) before committing locally,
   * making MATCH3 swap-spam feel laggy. The server's `validateSwap`
   * was geometric-only (bounds + adjacency) and added no real
   * authority that local state didn't already enforce.
   *
   * 3e behaviour: apply the swap to the local `M3Board` immediately
   * (zero-latency feel matching single-player), and append it to
   * `swapLog`. At MATCH3 → SCHED boundary the round's full
   * `(stats, swapLog)` is sent to the server, which replays it from
   * the per-team board seed and rejects the round if the claim
   * diverges from the replay (Step 4a shadow → 4b enforce).
   *
   * `M3Board.swap()` returns:
   *   - `null` if the board is animation-locked or coords are invalid
   *   - `true` on a successful (tactical) swap
   * Locked / invalid swaps are silently dropped — same as before —
   * and are NOT appended to the log so server replay sees only what
   * the client actually applied.
   *
   * The legacy `requestSwap` RPC is intentionally kept (server still
   * answers it) as a back-door in case Phase 5 production wants
   * per-swap validation back.
   */
  handleSwap(r1: number, c1: number, r2: number, c2: number): void {
    if (this.state !== GS.MATCH3) return;
    // Session 6c item 5: hard cap on swaps per round. Once we hit the
    // limit, drop new swap requests on the floor — the next update tick
    // will trigger _enterSched anyway via swapsExhausted().
    if (this.swapLog.length >= MAX_SWAPS_PER_ROUND) return;
    // Story Mode — block swaps during dialogue overlay, and during a
    // forced-swap tutorial step only the prescribed swap is accepted.
    if (this.storyController) {
      if (this.storyController.shouldPauseGame()) return;
      if (!this.storyController.isSwapAllowed(r1, c1, r2, c2)) return;
    }
    const ok = this.board.swap(r1, c1, r2, c2);
    if (ok === true) {
      this.swapLog.push({ team: this.myTeam, r1, c1, r2, c2 });
      if (this.storyController) {
        this.storyController.onSwapApplied(r1, c1, r2, c2);
        // Defer onMatchSettle until the cascade + anim queue drains;
        // update()'s MATCH3 branch polls for `!board.locked && !anim.busy`.
        this._storySettlePending = true;
      }
    }
  }

  /**
   * Session 6c: MATCH3 phase ends when EITHER timer expires OR swap quota
   * reached (whichever fires first). Called from `update()` MATCH3 case.
   */
  swapsExhausted(): boolean {
    return this.swapLog.length >= MAX_SWAPS_PER_ROUND;
  }
}

